<template>
  <section class="merchant-realtime-dashboard" :style="dashboardThemeStyle">
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <aside class="dashboard-sidebar" :class="{ collapsed: sidebarCollapsed }">
      <div class="sidebar-header">
        <div class="sidebar-brand" v-if="!sidebarCollapsed">Dashboard</div>
        <button
          class="sidebar-toggle"
          @click="sidebarCollapsed = !sidebarCollapsed"
        >
          <font-awesome-icon
            :icon="sidebarCollapsed ? 'angle-right' : 'angle-left'"
          />
        </button>
      </div>

      <button
        v-for="item in menuItems"
        :key="item.key"
        class="sidebar-item"
        :class="{ active: activeTab === item.key }"
        :title="item.label"
        @click="activeTab = item.key"
      >
        <font-awesome-icon :icon="item.icon" />
        <span v-if="!sidebarCollapsed">{{ item.label }}</span>
      </button>
    </aside>

    <main class="dashboard-content">
      <header class="dashboard-header">
        <div class="dashboard-title-row">
          <h1>{{ activeItem.label }}</h1>
          <div class="dashboard-scope-combobox">
            <input
              v-model="dashboardScopeSearch"
              class="dashboard-scope-input"
              type="text"
              @focus="openDashboardScopeDropdown"
              @input="handleDashboardScopeInput"
              @blur="closeDashboardScopeDropdown"
              @keydown.down.prevent="moveDashboardScopeHighlight(1)"
              @keydown.up.prevent="moveDashboardScopeHighlight(-1)"
              @keydown.enter.prevent="selectHighlightedDashboardScope"
              @keydown.esc="dashboardScopeDropdownOpen = false"
            />
            <div
              v-if="dashboardScopeDropdownOpen"
              class="dashboard-scope-dropdown"
            >
              <button
                v-for="(option, index) in filteredDashboardScopeOptions"
                :key="option.value"
                type="button"
                :class="{
                  active: isDashboardScopeOptionSelected(option),
                  highlighted: index === dashboardScopeHighlightedIndex
                }"
                @mouseenter="dashboardScopeHighlightedIndex = index"
                @mousedown.prevent="toggleDashboardScopeOption(option)"
              >
                <span class="dashboard-scope-check">
                  <font-awesome-icon
                    v-if="isDashboardScopeOptionSelected(option)"
                    icon="check"
                  />
                </span>
                {{ option.label }}
              </button>
            </div>
          </div>
          <div v-if="hasPeriodFilter" class="period-filter">
            <span>Período</span>
            <b-datepicker
              v-model="productsStartDate"
              class="period-date-picker"
              placeholder="Início"
              icon="calendar-day"
              :max-date="maxPeriodDate"
              :mobile-native="false"
              @input="handleDashboardPeriodChange"
            ></b-datepicker>
            <b-datepicker
              v-model="productsEndDate"
              class="period-date-picker"
              placeholder="Fim"
              icon="calendar-day"
              :max-date="maxPeriodDate"
              :mobile-native="false"
              @input="handleDashboardPeriodChange"
            ></b-datepicker>
            <template v-if="activeTab === 'sales'">
              <button
                type="button"
                class="sales-comparison-toggle"
                :class="{ active: salesComparisonEnabled }"
                @click="toggleSalesComparison"
              >
                Comparar com:
              </button>
              <b-datepicker
                ref="salesComparisonDatepicker"
                v-model="salesComparisonStartDate"
                :class="[
                  'period-date-picker',
                  'sales-comparison-date-picker',
                  { disabled: !salesComparisonEnabled }
                ]"
                placeholder="Início comparação"
                icon="calendar-day"
                :disabled="!salesComparisonEnabled"
                :date-formatter="formatSalesComparisonInputDate"
                :max-date="maxSalesComparisonStartDate"
                :mobile-native="false"
                :nearby-month-days="false"
                @input="handleSalesComparisonStartDateChange"
                @active-change="handleSalesComparisonDatepickerActiveChange"
              ></b-datepicker>
            </template>
          </div>
        </div>
        <div class="dashboard-header-actions">
          <button
            v-if="activeTab === 'sales'"
            type="button"
            class="dashboard-export-button"
            :disabled="isLoading"
            @click="getSalesDashboard"
          >
            <font-awesome-icon icon="sync-alt" :spin="isLoading" />
            Sync
          </button>
          <button
            v-if="activeTab === 'sales'"
            type="button"
            class="dashboard-export-button"
            @click="exportSalesDashboard"
          >
            <font-awesome-icon icon="file-download" />
            Excel
          </button>
          <button
            v-if="activeTab === 'products'"
            type="button"
            class="dashboard-export-button"
            :disabled="isLoading"
            @click="getProductsDashboard"
          >
            <font-awesome-icon icon="sync-alt" :spin="isLoading" />
            Sync
          </button>
          <button
            v-if="activeTab === 'products'"
            type="button"
            class="dashboard-export-button"
            @click="exportProductsDashboard"
          >
            <font-awesome-icon icon="file-download" />
            Excel
          </button>
          <span
            v-if="activeTab === 'realtime-transactions'"
            class="connection-status"
            :class="socketStatus"
          >
            {{ socketStatusLabel }}
          </span>
        </div>
      </header>

      <section
        v-if="activeTab === 'realtime-transactions'"
        class="dashboard-grid"
      >
        <div class="summary-strip">
          <div class="metric">
            <span>Faturamento Bruto</span>
            <strong>{{ formatCurrency(summary.grossSales) }}</strong>
          </div>
          <div class="metric">
            <span>Vendas</span>
            <strong>{{ formatNumber(summary.transactionsCount) }}</strong>
          </div>
          <div class="metric">
            <span>Ticket Médio</span>
            <strong>{{ formatCurrency(averageTicket) }}</strong>
          </div>
          <div class="metric">
            <span>Lojas Ativas</span>
            <strong>{{ formatNumber(activeStoresCount) }}</strong>
          </div>
          <div class="metric metric-chargeback">
            <span>
              Chargebacks:
              {{ formatNumber(summary.chargedbackTransactionsCount) }}
            </span>
            <strong>{{ formatCurrency(summary.chargedback) }}</strong>
          </div>
        </div>

        <aside class="map-panel">
          <div class="panel-title">
            <h2>Mapa de vendas</h2>
            <div class="map-title-actions">
              <span>{{ storesWithCoordinates.length }} lojas</span>
              <button
                type="button"
                class="map-control-button"
                :class="{ active: showMapPins }"
                @click="toggleMapPins"
              >
                Pins
              </button>
            </div>
          </div>
          <div class="leaflet-map-wrapper">
            <div ref="storesMap" class="stores-map"></div>
            <button
              type="button"
              class="map-center-button"
              title="Centralizar nas lojas"
              @click="centerLeafletOnStores"
            >
              <font-awesome-icon icon="crosshairs" />
            </button>
          </div>

          <div class="top-stores">
            <div class="panel-title small ranking-panel-title">
              <h2>{{ topRankingTitle }}</h2>
              <div v-if="topRankingModes.length" class="ranking-mode-toggle">
                <button
                  v-for="mode in topRankingModes"
                  :key="mode.key"
                  type="button"
                  :class="{ active: topRankingMode === mode.key }"
                  @click="topRankingMode = mode.key"
                >
                  {{ mode.label }}
                </button>
              </div>
            </div>
            <transition :name="rankingTransitionName" mode="out-in">
              <div :key="topRankingMode">
                <transition-group name="store-ranking" tag="div">
                  <div
                    v-for="(item, index) in rankedTopItems"
                    :key="item.id || item.name"
                    class="store-row"
                  >
                    <div class="store-info">
                      <span
                        class="store-position"
                        :style="getStoreRankStyle(index)"
                      >
                        {{ index + 1 }}
                      </span>
                      <span>
                        <template v-if="item.productCount">
                          {{ formatNumber(item.productCount) }}x
                        </template>
                        {{ item.name }}
                      </span>
                    </div>
                    <strong>
                      {{ formatCurrency(item.grossSales) }}
                    </strong>
                  </div>
                </transition-group>
              </div>
            </transition>
          </div>
        </aside>

        <div class="charts-column">
          <div class="transactions-panel">
            <div class="panel-title">
              <h2>Últimas transações</h2>
              <span>{{ recentTransactions.length }}/10</span>
            </div>

            <div v-if="recentTransactions.length === 0" class="empty-list">
              Aguardando vendas.
            </div>

            <transition-group name="transaction-list" tag="div">
              <div v-for="row in recentTransactionRows" :key="row.key">
                <div
                  v-if="row.type === 'date'"
                  class="transaction-date-divider"
                >
                  {{ row.label }}
                </div>
                <div v-else class="transaction-row">
                  <strong :title="row.transaction.storeName">
                    {{ row.transaction.storeName }}
                  </strong>
                  <div class="transaction-meta">
                    <span class="transaction-value">
                      {{ formatCurrency(row.transaction.value) }}
                    </span>
                    <span>{{ row.transaction.createdAtLabel }}</span>
                  </div>
                </div>
              </div>
            </transition-group>
          </div>

          <div class="chart-panel hourly-chart-panel">
            <div class="panel-title hourly-panel-title">
              <h2>Vendas por hora</h2>
              <span
                v-if="hourlyComparisonDelta"
                class="hourly-comparison-delta"
                :class="hourlyComparisonDelta.status"
              >
                {{ hourlyComparisonDelta.label }}
              </span>
              <span>{{ todayLabel }}</span>
              vs
              <b-datepicker
                v-model="comparisonDate"
                class="comparison-date-picker"
                placeholder="Comparar"
                icon="calendar-day"
                :max-date="maxComparisonDate"
                :mobile-native="false"
                @input="getTodayDashboard"
              ></b-datepicker>
            </div>
            <canvas ref="hourlyChart"></canvas>
          </div>

          <div class="chart-panel payment-chart-panel">
            <div class="panel-title payment-panel-title">
              <h2>Meios de pagamento</h2>
              <div class="payment-mode-toggle">
                <button
                  v-for="mode in paymentChartModes"
                  :key="mode.key"
                  type="button"
                  :class="{ active: paymentChartMode === mode.key }"
                  @click="setPaymentChartMode(mode.key)"
                >
                  {{ mode.label }}
                </button>
              </div>
            </div>
            <canvas ref="paymentChart"></canvas>
          </div>
        </div>
      </section>

      <section v-else-if="activeTab === 'sales'" class="sales-dashboard">
        <div
          class="sales-summary-strip"
          :class="{ comparing: salesComparisonEnabled }"
        >
          <div class="metric">
            <span>Faturamento Bruto</span>
            <strong>{{ formatCurrency(salesSummary.grossSales) }}</strong>
            <small
              v-if="salesComparisonEnabled"
              :class="salesDeltaClass(salesDeltas.grossSales)"
            >
              {{ formatDelta(salesDeltas.grossSales) }}
            </small>
          </div>
          <div class="metric">
            <span>Vendas</span>
            <strong>{{ formatNumber(salesSummary.transactionsCount) }}</strong>
            <small
              v-if="salesComparisonEnabled"
              :class="salesDeltaClass(salesDeltas.transactionsCount)"
            >
              {{ formatDelta(salesDeltas.transactionsCount) }}
            </small>
          </div>
          <div class="metric">
            <span>Ticket Médio</span>
            <strong>{{ formatCurrency(salesAverageTicket) }}</strong>
            <small
              v-if="salesComparisonEnabled"
              :class="salesDeltaClass(salesDeltas.averageTicket)"
            >
              {{ formatDelta(salesDeltas.averageTicket) }}
            </small>
          </div>
          <div class="metric">
            <span>Lojas Ativas</span>
            <strong>{{ formatNumber(salesSummary.activeStores) }}</strong>
            <small
              v-if="salesComparisonEnabled"
              :class="salesDeltaClass(salesDeltas.activeStores)"
            >
              {{ formatDelta(salesDeltas.activeStores) }}
            </small>
          </div>
          <div class="metric metric-chargeback">
            <span>
              Chargebacks:
              {{ formatNumber(salesSummary.chargedbackTransactionsCount) }}
            </span>
            <strong>{{ formatCurrency(salesSummary.chargedback) }}</strong>
            <small
              v-if="salesComparisonEnabled"
              :class="salesDeltaClass(salesDeltas.chargedback)"
            >
              {{ formatDelta(salesDeltas.chargedback) }}
            </small>
          </div>
        </div>

        <div class="sales-charts-grid">
          <div class="chart-panel sales-daily-chart-panel">
            <div class="panel-title sales-daily-panel-title">
              <h2>Faturamento por dia</h2>
              <div class="ranking-mode-toggle">
                <button
                  v-for="mode in visibleSalesDailyGroupModes"
                  :key="mode.key"
                  type="button"
                  :class="{ active: selectedSalesDailyGroupMode === mode.key }"
                  @click="salesDailyGroupMode = mode.key"
                >
                  {{ mode.label }}
                </button>
              </div>
            </div>
            <canvas ref="salesDailyChart"></canvas>
          </div>

          <div class="chart-panel sales-payment-chart-panel">
            <div class="panel-title payment-panel-title">
              <h2>Meios de pagamento</h2>
              <span class="payment-total">
                {{ formatCurrency(salesPaymentTotal) }}
              </span>
              <div class="payment-mode-toggle">
                <button
                  v-for="mode in paymentChartModes"
                  :key="`sales-${mode.key}`"
                  type="button"
                  :class="{ active: paymentChartMode === mode.key }"
                  @click="setPaymentChartMode(mode.key)"
                >
                  {{ mode.label }}
                </button>
              </div>
            </div>
            <canvas ref="salesPaymentChart"></canvas>
          </div>

          <div class="chart-panel sales-hourly-chart-panel">
            <div class="panel-title sales-time-panel-title">
              <h2>{{ salesTimeChartTitle }}</h2>
              <div class="ranking-mode-toggle">
                <button
                  type="button"
                  :class="{ active: salesTimeMode === 'hour' }"
                  @click="salesTimeMode = 'hour'"
                >
                  Hora
                </button>
                <button
                  type="button"
                  :class="{ active: salesTimeMode === 'weekDay' }"
                  @click="salesTimeMode = 'weekDay'"
                >
                  Dia da semana
                </button>
              </div>
            </div>
            <canvas ref="salesHourlyChart"></canvas>
          </div>

          <div class="chart-panel sales-top-stores-panel">
            <div class="panel-title">
              <h2>Top lojas no período</h2>
              <span>{{ salesTopStores.length }} lojas</span>
            </div>
            <div v-if="!salesTopStores.length" class="empty-list">
              Sem vendas no período.
            </div>
            <canvas v-else ref="salesTopStoresChart"></canvas>
          </div>
        </div>
      </section>

      <section v-else-if="activeTab === 'products'" class="products-dashboard">
        <div class="products-summary-strip">
          <div class="metric">
            <span>Faturamento Bruto</span>
            <strong>{{ formatCurrency(productsSummary.grossSales) }}</strong>
          </div>
          <div class="metric">
            <span>Itens vendidos</span>
            <strong>{{ formatNumber(productsSummary.itemsSold) }}</strong>
          </div>
          <div class="metric">
            <span>SKUs vendidos</span>
            <strong>{{ formatNumber(productsSummary.skusSold) }}</strong>
          </div>
        </div>

        <div class="products-charts-grid">
          <div class="chart-panel products-chart-panel">
            <div class="panel-title product-chart-title">
              <div class="matrix-title-line">
                <span class="abc-matrix-help-tooltip-wrap">
                  <font-awesome-icon
                    class="abc-matrix-help-icon"
                    icon="info-circle"
                  />
                  <span class="abc-matrix-help-tooltip">
                    Este gráfico mostra o ranking de produtos, categorias ou
                    subcategorias no período selecionado. A curva ABC organiza
                    os itens pela representatividade acumulada: classe A até
                    80%, classe B até 95% e classe C até 100%. A linha preta
                    mostra o percentual acumulado.
                  </span>
                </span>
                <h2>{{ productsChartTitle }} + Curva ABC</h2>
              </div>
              <div class="product-chart-controls">
                <div class="ranking-mode-toggle">
                  <button
                    v-for="mode in productDimensionModes"
                    :key="mode.key"
                    type="button"
                    :class="{ active: productDimensionMode === mode.key }"
                    @click="setProductDimensionMode(mode.key)"
                  >
                    {{ mode.label }}
                  </button>
                </div>
                <div class="ranking-mode-toggle">
                  <button
                    v-for="mode in productMetricModes"
                    :key="mode.key"
                    type="button"
                    :class="{ active: productMetricMode === mode.key }"
                    @click="setProductMetricMode(mode.key)"
                  >
                    {{ mode.label }}
                  </button>
                </div>
              </div>
            </div>
            <div class="top-products-sticky-scale">
              <span></span>
              <div
                class="top-products-percentage-scale"
                :style="topProductsScaleStyle"
              >
                <span
                  v-for="tick in percentageScaleTicks"
                  :key="tick"
                  :style="{ left: `${tick}%` }"
                >
                  {{ tick }}%
                </span>
                <strong>% acumulado</strong>
              </div>
            </div>
            <div class="top-products-chart-scroll">
              <div
                class="top-products-chart-canvas"
                :style="{ height: `${topProductsChartHeight}px` }"
              >
                <canvas ref="topProductsChart"></canvas>
              </div>
            </div>
            <div class="abc-summary">
              <div
                v-for="item in abcSummary"
                :key="item.key"
                class="abc-summary-item"
              >
                <span :style="{ background: item.color }"></span>
                <strong>{{ item.key }}</strong>
                <small>{{ item.count }} itens</small>
              </div>
            </div>
          </div>

          <div class="chart-panel products-matrix-panel">
            <div class="panel-title product-chart-title">
              <div>
                <div class="matrix-title-line">
                  <span class="abc-matrix-help-tooltip-wrap">
                    <font-awesome-icon
                      class="abc-matrix-help-icon"
                      icon="info-circle"
                    />
                    <span class="abc-matrix-help-tooltip">
                      <!-- Não altere este texto -->
                      A matriz é um cruzamento da curva ABC de quantidade com a
                      de faturamento. Ela mostra a quantidade de itens em cada
                      categoria e o percentual de representatividade de cada
                      uma. Os itens de classe A em quantidade são os principais
                      produtos que não podem faltar na loja, pois são os que
                      mais trazem clientes para a loja.
                    </span>
                  </span>
                  <h2>Matriz ABC</h2>
                  <span class="abc-reliability-tooltip-wrap">
                    <span
                      class="abc-reliability-tag"
                      :class="abcMatrixReliability.status"
                    >
                      {{ abcMatrixReliability.label }}
                    </span>
                    <span class="abc-reliability-tooltip">
                      {{ abcMatrixReliability.tooltip }}
                    </span>
                  </span>
                </div>
              </div>
              <div class="product-chart-controls">
                <div class="ranking-mode-toggle">
                  <button
                    v-for="mode in productDimensionModes"
                    :key="`matrix-${mode.key}`"
                    type="button"
                    :class="{ active: matrixDimensionMode === mode.key }"
                    @click="matrixDimensionMode = mode.key"
                  >
                    {{ mode.label }}
                  </button>
                </div>
              </div>
            </div>

            <div
              v-if="abcMatrixReliability.status !== 'none'"
              class="abc-matrix-body"
            >
              <div class="abc-matrix">
                <div class="abc-matrix-corner">
                  <span>Qtd.</span>
                  <strong>Fat.</strong>
                </div>
                <div
                  v-for="column in abcMatrixClasses"
                  :key="`column-${column}`"
                  class="abc-matrix-header"
                >
                  {{ column }}
                </div>

                <template v-for="row in abcMatrixRows">
                  <div :key="`row-${row.key}`" class="abc-matrix-row-label">
                    {{ row.key }}
                  </div>
                  <button
                    v-for="cell in row.cells"
                    :key="cell.key"
                    type="button"
                    class="abc-matrix-cell"
                    :class="cell.priority"
                    :data-revenue-class="cell.revenueClass"
                    :aria-label="cell.title"
                  >
                    <strong class="abc-matrix-category">{{ cell.key }}</strong>
                    <span class="abc-matrix-category-tooltip">
                      {{ cell.description }}
                    </span>
                    <span>{{ cell.count }}</span>
                    <small>{{ cell.percentage }}</small>
                  </button>
                </template>
              </div>

              <button
                type="button"
                class="abc-matrix-items-button"
                @click="showAbcMatrixItemsModal = true"
              >
                Ver itens
              </button>
            </div>

            <div
              v-if="abcMatrixReliability.status !== 'none'"
              class="abc-matrix-legend"
            >
              <div class="abc-matrix-color-legend">
                <span class="abc-color-dot critical"></span>
                <strong>Prioridade máxima</strong>
                <small>alto giro e alto faturamento</small>
              </div>
              <div class="abc-matrix-color-legend">
                <span class="abc-color-dot strategic"></span>
                <strong>Atenção estratégica</strong>
                <small>alto em uma dimensão e médio/baixo na outra</small>
              </div>
              <div class="abc-matrix-color-legend">
                <span class="abc-color-dot middle"></span>
                <strong>Intermediários</strong>
                <small>desempenho sem alerta principal</small>
              </div>
              <div class="abc-matrix-color-legend">
                <span class="abc-color-dot low"></span>
                <strong>Baixa prioridade</strong>
                <small>baixo giro e baixo faturamento</small>
              </div>
            </div>
            <div v-else class="abc-matrix-empty">
              A matriz será exibida quando houver pelo menos 30 unidades
              vendidas e 3 itens no agrupamento selecionado.
            </div>
          </div>
        </div>
      </section>

      <section v-else></section>
    </main>

    <b-modal
      :active.sync="showAbcMatrixItemsModal"
      class="abc-matrix-items-modal-root"
      has-modal-card
      trap-focus
    >
      <div class="modal-card abc-matrix-items-modal">
        <header class="modal-card-head">
          <p class="modal-card-title">Itens da Matriz ABC</p>
        </header>
        <section class="modal-card-body">
          <div
            v-for="cell in abcMatrixFlatCells"
            :key="`modal-${cell.key}`"
            class="abc-matrix-items-group"
          >
            <div class="abc-matrix-items-group-title" :class="cell.priority">
              <strong>{{ cell.key }}</strong>
              <div class="abc-matrix-items-group-copy">
                <small>
                  {{ formatNumber(cell.count) }} itens ·
                  {{ cell.percentage }}
                </small>
                <span>{{ cell.description }}</span>
              </div>
            </div>
            <div v-if="cell.items.length" class="abc-matrix-items-list">
              <div
                v-for="item in cell.items"
                :key="`${cell.key}-${item.id || item.name}`"
                class="abc-matrix-items-row"
              >
                <span>{{ item.name }}</span>
                <small>
                  {{ formatNumber(item.productCount) }} un ·
                  {{ formatCurrency(item.grossSales) }}
                </small>
              </div>
            </div>
            <div v-else class="abc-matrix-items-empty">Sem itens</div>
          </div>
        </section>
        <footer class="modal-card-foot abc-matrix-items-footer">
          <div class="ranking-mode-toggle">
            <button
              v-for="mode in productDimensionModes"
              :key="`modal-matrix-${mode.key}`"
              type="button"
              :class="{ active: matrixDimensionMode === mode.key }"
              @click="matrixDimensionMode = mode.key"
            >
              {{ mode.label }}
            </button>
          </div>
        </footer>
      </div>
    </b-modal>
  </section>
</template>

<script>
import Chart from "chart.js";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { io } from "socket.io-client";
import { mapGetters } from "vuex";
import XLSX from "xlsx-js-style";
import { common } from "../../mixins/common";

const MAX_RECENT_TRANSACTIONS = 10;
const MAX_PRODUCT_ABC_BARS = 200;
const PAYMENT_CHART_MODES = [
  { key: "channel", label: "Canal" },
  { key: "type", label: "Tipo" },
  { key: "breakdown", label: "Detalhado" }
];
const PAYMENT_CHART_COLORS = {
  pos: "#11a36a",
  posCredit: "#0f8f5d",
  posDebit: "#39c982",
  tef: "#f0512d",
  tefCredit: "#e34a2b",
  tefDebit: "#ff8a65",
  applePay: "#38bdf8",
  applePayCredit: "#0ea5e9",
  applePayDebit: "#7dd3fc",
  googlePay: "#ffb020",
  googlePayCredit: "#f59e0b",
  googlePayDebit: "#fcd34d",
  pix: "#00a6a6",
  pixCelcoin: "#008c8c",
  pixPagbank: "#18b7b7",
  pixAdyen: "#5ed7d7",
  credit: "#f0512d",
  debit: "#11a36a",
  appCredit: "#e64a6a",
  prepaid: "#64748b",
  voucher: "#7c3aed",
  discounts: "#8b5cf6",
  other: "#98a2b3"
};
const VOUCHER_PAYMENT_KEYS = [
  "voucher",
  "voucherSodexo",
  "voucherAlelo",
  "voucherTicket",
  "voucherVr",
  "voucherVerocard",
  "voucherBen"
];
const TOP_STORES_CHART_COLORS = [
  "#11a36a",
  "#ffb020",
  "#f0512d",
  "#38bdf8",
  "#7c3aed",
  "#00a6a6",
  "#e64a6a",
  "#64748b",
  "#98a2b3",
  "#344054"
];

export default {
  mixins: [common],

  data() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 6);
    const comparisonStartDate = new Date(startDate);
    comparisonStartDate.setDate(startDate.getDate() - 7);
    const defaultComparisonDate = new Date(endDate);
    defaultComparisonDate.setDate(endDate.getDate() - 7);

    return {
      isLoading: false,
      sidebarCollapsed: true,
      activeTab: "realtime-transactions",
      selectedStoreIds: [],
      dashboardScopeSearch: "Todas as lojas",
      dashboardScopeDropdownOpen: false,
      dashboardScopeSearchDirty: false,
      dashboardScopeHighlightedIndex: 0,
      comparisonDate: defaultComparisonDate,
      dashboardBISettingsSaveTimeout: null,
      isApplyingDashboardBISettings: false,
      socket: null,
      socketStatus: "disconnected",
      hourlyChart: null,
      paymentChart: null,
      salesDailyChart: null,
      salesHourlyChart: null,
      salesPaymentChart: null,
      salesTopStoresChart: null,
      topProductsChart: null,
      topProductsScaleInsets: {
        left: 160,
        right: 56
      },
      paymentChartMode: "channel",
      paymentChartModes: PAYMENT_CHART_MODES,
      map: null,
      savedLeafletView: null,
      restoreLeafletViewAfterLoad: false,
      storeMarkersLayer: null,
      pulseLayer: null,
      showMapPins: true,
      summary: {
        transactionsCount: 0,
        grossSales: 0,
        netSales: 0,
        discounts: 0,
        chargedback: 0,
        chargedbackTransactionsCount: 0,
        activeStores: 0,
        salesByHour: [],
        comparisonSalesByHour: [],
        payments: {}
      },
      topStores: [],
      topProducts: [],
      topCategories: [],
      topSubcategories: [],
      topRankingMode: "stores",
      topRankingModes: [
        { key: "stores", label: "Lojas" },
        { key: "products", label: "Produtos" },
        { key: "categories", label: "Categorias" },
        { key: "subcategories", label: "Subcategorias" }
      ],
      activeStoreIds: [],
      mapStores: [],
      productsStartDate: startDate,
      productsEndDate: endDate,
      salesSummary: {
        transactionsCount: 0,
        grossSales: 0,
        netSales: 0,
        discounts: 0,
        chargedback: 0,
        chargedbackTransactionsCount: 0,
        activeStores: 0
      },
      salesComparisonSummary: {
        transactionsCount: 0,
        grossSales: 0,
        netSales: 0,
        discounts: 0,
        chargedback: 0,
        chargedbackTransactionsCount: 0,
        activeStores: 0
      },
      salesComparisonEnabled: true,
      salesComparisonStartDate: comparisonStartDate,
      salesComparisonPreviewStartDate: null,
      salesComparisonDatepickerMouseoverHandler: null,
      salesComparisonDatepickerMouseleaveHandler: null,
      salesComparisonCustomStartDate: false,
      salesDailyGroupMode: "day",
      salesDailyGroupModes: [
        { key: "day", label: "Dia" },
        { key: "week", label: "Semana" },
        { key: "month", label: "Mês" }
      ],
      salesTimeMode: "hour",
      salesByDay: [],
      salesComparisonByDay: [],
      salesByHour: [],
      salesComparisonByHour: [],
      salesTopStores: [],
      salesComparisonPeriod: {
        startDate: null,
        endDate: null
      },
      productsSummary: {
        itemsSold: 0,
        skusSold: 0,
        grossSales: 0
      },
      productRows: [],
      productMetricMode: "quantity",
      productDimensionMode: "product",
      matrixDimensionMode: "product",
      showAbcMatrixItemsModal: false,
      productMetricModes: [
        { key: "quantity", label: "Qtd." },
        { key: "revenue", label: "Faturamento" }
      ],
      productDimensionModes: [
        { key: "product", label: "Produto" },
        { key: "category", label: "Categoria" },
        { key: "subcategory", label: "Subcategoria" }
      ],
      recentTransactions: [],
      menuItems: [
        {
          key: "realtime-transactions",
          label: "Transações tempo real",
          icon: "satellite-dish"
        },
        { key: "sales", label: "Vendas", icon: "list" },
        { key: "products", label: "Produtos", icon: "tag" }
      ]
    };
  },

  computed: {
    ...mapGetters(["sessionToken", "preferences", "categories", "stores"]),

    dashboardStores() {
      return this.stores.map(store => {
        return {
          storeId: store._id,
          storeName:
            store.storeInfo && store.storeInfo.name
              ? store.storeInfo.name
              : "Loja sem nome"
        };
      });
    },

    activeItem() {
      return (
        this.menuItems.find(item => item.key === this.activeTab) ||
        this.menuItems[0]
      );
    },

    hasPeriodFilter() {
      return ["sales", "products"].includes(this.activeTab);
    },

    dashboardPrimaryColor() {
      return this.$primaryColor || "#f0512d";
    },

    dashboardPrimaryTextColor() {
      const rgb = this.hexToRgb(this.dashboardPrimaryColor);
      if (!rgb) return this.dashboardPrimaryColor;

      const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
      return brightness > 170 ? "#172033" : this.dashboardPrimaryColor;
    },

    dashboardNavbarColor() {
      if (this.$chain === "mq") return "#133b23";
      if (this.$chain === "brmania") return "#3A8349";
      return "#08080a";
    },

    dashboardThemeStyle() {
      const rgb = this.hexToRgb(this.dashboardPrimaryColor) || {
        r: 240,
        g: 81,
        b: 45
      };

      return {
        "--dashboard-navbar": this.dashboardNavbarColor,
        "--dashboard-primary": this.dashboardPrimaryColor,
        "--dashboard-primary-contrast": this.$primaryColorContrast || "#ffffff",
        "--dashboard-primary-text": this.dashboardPrimaryTextColor,
        "--dashboard-selected-text": "#172033",
        "--dashboard-primary-soft": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`,
        "--dashboard-primary-focus": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.14)`,
        "--dashboard-primary-muted": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.18)`,
        "--dashboard-primary-pulse": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`
      };
    },

    dashboardScopeParams() {
      const params = {};

      if (this.selectedStoreIds.length) {
        params.storeIds = this.selectedStoreIds;
      }

      if (this.comparisonDate) {
        params.comparisonDate = this.moment(this.comparisonDate).format(
          "YYYY-MM-DD"
        );
      }

      return params;
    },

    productsDashboardParams() {
      const params = {};

      if (this.selectedStoreIds.length) {
        params.storeIds = this.selectedStoreIds;
      }

      params.startDate = this.moment(this.productsStartDate).format(
        "YYYY-MM-DD"
      );
      params.endDate = this.moment(this.productsEndDate).format("YYYY-MM-DD");

      return params;
    },

    salesDashboardParams() {
      const params = Object.assign({}, this.productsDashboardParams);
      if (this.salesComparisonEnabled && this.salesComparisonStartDate) {
        params.comparisonStartDate = this.moment(
          this.salesComparisonStartDate
        ).format("YYYY-MM-DD");
      }
      return params;
    },

    dashboardScopeOptions() {
      return [{ value: "all", label: "Todas as lojas" }].concat(
        this.dashboardStores
          .slice()
          .sort((a, b) => {
            return a.storeName.localeCompare(b.storeName);
          })
          .map(store => {
            return {
              value: `store:${store.storeId}`,
              label: store.storeName
            };
          })
      );
    },

    filteredDashboardScopeOptions() {
      if (!this.dashboardScopeSearchDirty) return this.dashboardScopeOptions;

      const search = this.dashboardScopeSearch.trim().toLowerCase();
      if (!search) return this.dashboardScopeOptions;

      return this.dashboardScopeOptions.filter(option => {
        return option.label.toLowerCase().includes(search);
      });
    },

    dashboardScopeLabel() {
      if (!this.selectedStoreIds.length) return "Todas as lojas";
      if (this.selectedStoreIds.length === 1) {
        const store = this.dashboardStores.find(item => {
          return String(item.storeId) === this.selectedStoreIds[0];
        });
        return store ? store.storeName : "1 loja selecionada";
      }
      return `${this.selectedStoreIds.length} lojas selecionadas`;
    },

    todayLabel() {
      return this.moment().format("DD/MM/YYYY");
    },

    maxComparisonDate() {
      return this.moment()
        .subtract(1, "day")
        .toDate();
    },

    maxPeriodDate() {
      return this.moment()
        .endOf("day")
        .toDate();
    },

    salesPeriodDays() {
      if (!this.productsStartDate || !this.productsEndDate) return 1;
      return (
        this.moment(this.productsEndDate).diff(
          this.moment(this.productsStartDate),
          "days"
        ) + 1
      );
    },

    salesGroupingPeriodDays() {
      if (!this.productsStartDate || !this.productsEndDate) return 0;
      return this.moment(this.productsEndDate).diff(
        this.moment(this.productsStartDate),
        "days"
      );
    },

    effectiveSalesDailyGroupMode() {
      return this.selectedSalesDailyGroupMode;
    },

    selectedSalesDailyGroupMode() {
      if (this.isSalesDailyGroupModeAvailable(this.salesDailyGroupMode)) {
        return this.salesDailyGroupMode;
      }
      return this.defaultSalesDailyGroupMode;
    },

    defaultSalesDailyGroupMode() {
      return "day";
    },

    visibleSalesDailyGroupModes() {
      return this.salesDailyGroupModes.filter(mode => {
        if (mode.key === "day") return this.salesGroupingPeriodDays >= 7;
        if (mode.key === "week") return this.salesGroupingPeriodDays >= 7;
        if (mode.key === "month") return this.salesGroupingPeriodDays >= 31;
        return false;
      });
    },

    maxSalesComparisonStartDate() {
      if (!this.productsStartDate) return this.maxComparisonDate;
      return this.getDefaultSalesComparisonStartDate();
    },

    comparisonDateLabel() {
      if (!this.comparisonDate) return "";
      return this.moment(this.comparisonDate).format("DD/MM/YYYY");
    },

    hourlyComparisonDelta() {
      if (!this.comparisonDate) return null;

      const currentHour = Number(this.moment().format("H"));
      const lastClosedHour = currentHour - 1;
      if (lastClosedHour < 0) return null;

      const todayTotal = this.sumSalesByHourUntil(
        this.summary.salesByHour,
        lastClosedHour
      );
      const comparisonTotal = this.sumSalesByHourUntil(
        this.summary.comparisonSalesByHour,
        lastClosedHour
      );
      if (!comparisonTotal) return null;

      const delta = ((todayTotal - comparisonTotal) / comparisonTotal) * 100;
      const signal = delta > 0 ? "+" : "";

      return {
        status: delta >= 0 ? "positive" : "negative",
        label: `${signal}${delta.toFixed(1)}% até ${currentHour}h`
      };
    },

    averageTicket() {
      if (!this.summary.transactionsCount) return 0;
      return this.summary.grossSales / this.summary.transactionsCount;
    },

    salesPaymentTotal() {
      const netSales = Number(this.salesSummary.netSales || 0);
      if (netSales) return netSales;
      return this.getPaymentTotalForChart(
        this.getPaymentChartData(this.salesSummary)
      );
    },

    salesAverageTicket() {
      if (!this.salesSummary.transactionsCount) return 0;
      return this.salesSummary.grossSales / this.salesSummary.transactionsCount;
    },

    salesComparisonAverageTicket() {
      if (!this.salesComparisonSummary.transactionsCount) return 0;
      return (
        this.salesComparisonSummary.grossSales /
        this.salesComparisonSummary.transactionsCount
      );
    },

    salesDeltas() {
      return {
        grossSales: this.getDeltaPercentage(
          this.salesSummary.grossSales,
          this.salesComparisonSummary.grossSales
        ),
        transactionsCount: this.getDeltaPercentage(
          this.salesSummary.transactionsCount,
          this.salesComparisonSummary.transactionsCount
        ),
        averageTicket: this.getDeltaPercentage(
          this.salesAverageTicket,
          this.salesComparisonAverageTicket
        ),
        activeStores: this.getDeltaPercentage(
          this.salesSummary.activeStores,
          this.salesComparisonSummary.activeStores
        ),
        chargedback: this.getDeltaPercentage(
          this.salesSummary.chargedback,
          this.salesComparisonSummary.chargedback
        )
      };
    },

    salesPeriodLabel() {
      return `${this.moment(this.productsStartDate).format(
        "DD/MM"
      )} - ${this.moment(this.productsEndDate).format("DD/MM")}`;
    },

    salesComparisonPeriodLabel() {
      if (!this.salesComparisonStartDate) {
        return "período anterior";
      }

      return `${this.moment(this.salesComparisonStartDate).format("DD/MM")} - ${
        this.salesComparisonEndDateLabel
      }`;
    },

    salesComparisonEndDateLabel() {
      if (!this.salesComparisonStartDate) return "";
      return this.moment(this.salesComparisonStartDate)
        .add(this.salesPeriodDays - 1, "days")
        .format("DD/MM");
    },

    salesComparisonDisplayedPeriodLabel() {
      const startDate =
        this.salesComparisonPreviewStartDate || this.salesComparisonStartDate;
      if (!startDate) return "período anterior";

      const endDate = this.moment(startDate)
        .add(this.salesPeriodDays - 1, "days")
        .format("DD/MM");
      return `${this.moment(startDate).format("DD/MM")} - ${endDate}`;
    },

    salesTimeChartTitle() {
      return this.salesTimeMode === "weekDay"
        ? "Vendas por dia da semana"
        : "Vendas por hora";
    },

    salesByWeekDay() {
      return this.getSalesWeekDayRows(this.salesByDay);
    },

    activeStoresCount() {
      return Number(
        this.summary.activeStores || this.activeStoreIds.length || 0
      );
    },

    storesWithCoordinates() {
      return this.mapStores.filter(store =>
        this.parseCoordinates(store.coordinates)
      );
    },

    rankedTopStores() {
      return this.topStores
        .slice()
        .sort((a, b) => Number(b.grossSales || 0) - Number(a.grossSales || 0))
        .slice(0, 10);
    },

    rankedTopProducts() {
      return this.topProducts
        .slice()
        .sort((a, b) => Number(b.grossSales || 0) - Number(a.grossSales || 0))
        .slice(0, 10);
    },

    rankedTopCategories() {
      return this.topCategories
        .slice()
        .sort((a, b) => Number(b.grossSales || 0) - Number(a.grossSales || 0))
        .slice(0, 10);
    },

    rankedTopSubcategories() {
      return this.topSubcategories
        .slice()
        .sort((a, b) => Number(b.grossSales || 0) - Number(a.grossSales || 0))
        .slice(0, 10);
    },

    rankedTopItems() {
      if (this.topRankingMode === "products") {
        return this.rankedTopProducts.map(product => {
          return {
            id: product.productId || product.productName,
            name: product.productName,
            grossSales: product.grossSales,
            productCount: product.productCount
          };
        });
      }

      if (this.topRankingMode === "categories") {
        return this.rankedTopCategories.map(category => {
          return {
            id: category.category || category.name,
            name: this.categoryNameById[category.category] || "Sem categoria",
            grossSales: category.grossSales,
            productCount: category.productCount
          };
        });
      }

      if (this.topRankingMode === "subcategories") {
        return this.rankedTopSubcategories.map(subcategory => {
          return {
            id: subcategory.subcategory || subcategory.name,
            name:
              this.subcategoryNameById[subcategory.subcategory] ||
              "Sem subcategoria",
            grossSales: subcategory.grossSales,
            productCount: subcategory.productCount
          };
        });
      }

      return this.rankedTopStores.map(store => {
        return {
          id: store.storeId || store.storeName,
          name: store.storeName,
          grossSales: store.grossSales
        };
      });
    },

    topRankingTitle() {
      return "Top 10";
    },

    rankingTransitionName() {
      return this.topRankingMode !== "stores"
        ? "store-ranking-forward"
        : "store-ranking-back";
    },

    socketStatusLabel() {
      if (this.socketStatus === "connected") return "conectado";
      if (this.socketStatus === "connecting") return "conectando";
      return "desconectado";
    },

    recentTransactionRows() {
      const rows = [];
      let currentDate = null;

      this.recentTransactions.forEach(transaction => {
        const dateKey = transaction.createdAt.format("YYYY-MM-DD");
        if (dateKey !== currentDate) {
          currentDate = dateKey;
          rows.push({
            type: "date",
            key: `date-${dateKey}`,
            label: transaction.createdAt.format("DD/MM/YYYY")
          });
        }

        rows.push({
          type: "transaction",
          key: transaction.id,
          transaction
        });
      });

      return rows;
    },

    categoryNameById() {
      const names = {};
      (this.categories || []).forEach(category => {
        names[String(category._id)] = category.name;
      });
      return names;
    },

    subcategoryNameById() {
      const names = {};
      (this.categories || []).forEach(category => {
        (category.subcategories || []).forEach(subcategory => {
          names[String(subcategory._id)] = subcategory.name;
        });
      });
      return names;
    },

    topProductsChartRows() {
      return this.getVisibleProductAbcRows(this.fullTopProductsAbcRows);
    },

    fullTopProductsAbcRows() {
      const metric = this.productMetricMode;
      const rows = this.getGroupedProductRows(this.productDimensionMode).sort(
        (a, b) =>
          this.getProductMetricValue(b, metric) -
          this.getProductMetricValue(a, metric)
      );
      return this.getProductsAbcRows(rows, metric);
    },

    topProductsChartHeight() {
      return Math.max(220, this.topProductsChartRows.length * 28 + 28);
    },

    percentageScaleTicks() {
      return [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    },

    abcMatrixClasses() {
      return ["A", "B", "C"];
    },

    topProductsScaleStyle() {
      return {
        marginLeft: `${this.topProductsScaleInsets.left}px`,
        marginRight: `${this.topProductsScaleInsets.right}px`
      };
    },

    productMetricLabel() {
      return this.productMetricMode === "revenue" ? "Valor" : "Quantidade";
    },

    productsChartTitle() {
      const titles = {
        product: "Top produtos",
        category: "Top categorias",
        subcategory: "Top subcategorias"
      };
      return titles[this.productDimensionMode] || titles.product;
    },

    abcSummary() {
      const summary = {
        A: { key: "A", count: 0, color: this.getAbcColor("A") },
        B: { key: "B", count: 0, color: this.getAbcColor("B") },
        C: { key: "C", count: 0, color: this.getAbcColor("C") }
      };

      this.fullTopProductsAbcRows.forEach(item => {
        if (summary[item.abcClass]) summary[item.abcClass].count += 1;
      });

      return [summary.A, summary.B, summary.C];
    },

    abcMatrixGroupedRows() {
      return this.getGroupedProductRows(this.matrixDimensionMode);
    },

    abcMatrixReliability() {
      const totalUnits = this.abcMatrixGroupedRows.reduce((total, row) => {
        return total + Number(row.productCount || 0);
      }, 0);
      const groups = this.abcMatrixGroupedRows.length;
      const volume = `Volume atual: ${this.formatNumber(
        totalUnits
      )} unidades vendidas e ${this.formatNumber(groups)} grupos.`;

      if (totalUnits < 30 || groups < 3) {
        return {
          status: "none",
          label: "Amostra insuficiente",
          description:
            "Selecione mais lojas ou um período maior para exibir uma matriz confiável.",
          tooltip: `${volume} Não confiável: menos de 30 unidades vendidas ou menos de 3 grupos. A matriz fica oculta.`
        };
      }

      if (totalUnits < 100 || groups < 5) {
        return {
          status: "low",
          label: "Baixa confiabilidade",
          description:
            "Use como sinal inicial. Para decisão de mix, prefira um período maior.",
          tooltip: `${volume} Baixa confiabilidade: menos de 100 unidades vendidas ou menos de 5 grupos. Use como sinal inicial e prefira um período maior para decisão de mix.`
        };
      }

      if (totalUnits < 300) {
        return {
          status: "medium",
          label: "Média confiabilidade",
          description:
            "Boa leitura direcional, ainda sensível a picos pontuais.",
          tooltip: `${volume} Média confiabilidade: entre 100 e 299 unidades vendidas, com pelo menos 5 grupos. Boa leitura direcional, mas ainda sensível a picos pontuais.`
        };
      }

      return {
        status: "high",
        label: "Alta confiabilidade",
        description:
          "Volume suficiente para uma leitura operacional consistente.",
        tooltip: `${volume} Alta confiabilidade: 300 ou mais unidades vendidas, com pelo menos 5 grupos. Volume suficiente para uma leitura operacional consistente.`
      };
    },

    abcMatrixRows() {
      const groupedRows = this.abcMatrixGroupedRows;
      const quantityRows = this.getProductsAbcRows(
        groupedRows
          .slice()
          .sort(
            (a, b) =>
              this.getProductMetricValue(b, "quantity") -
              this.getProductMetricValue(a, "quantity")
          ),
        "quantity"
      );
      const revenueRows = this.getProductsAbcRows(
        groupedRows
          .slice()
          .sort(
            (a, b) =>
              this.getProductMetricValue(b, "revenue") -
              this.getProductMetricValue(a, "revenue")
          ),
        "revenue"
      );
      const revenueClassById = revenueRows.reduce((classes, row) => {
        classes[row.id] = row.abcClass;
        return classes;
      }, {});
      const matrix = this.abcMatrixClasses.reduce((rows, quantityClass) => {
        rows[quantityClass] = this.abcMatrixClasses.reduce(
          (columns, revenueClass) => {
            columns[revenueClass] = [];
            return columns;
          },
          {}
        );
        return rows;
      }, {});

      quantityRows.forEach(row => {
        const quantityClass = row.abcClass;
        const revenueClass = revenueClassById[row.id] || "C";
        matrix[quantityClass][revenueClass].push(row);
      });

      const total = quantityRows.length || 1;
      return this.abcMatrixClasses.map(quantityClass => {
        return {
          key: quantityClass,
          cells: this.abcMatrixClasses.map(revenueClass => {
            const items = matrix[quantityClass][revenueClass];
            const key = `${quantityClass}${revenueClass}`;
            return {
              key,
              revenueClass,
              count: items.length,
              percentage: `${((items.length / total) * 100).toFixed(0)}%`,
              priority: this.getAbcMatrixPriority(key),
              description: this.getAbcMatrixDescription(key),
              items: items.slice().sort((a, b) => {
                return (
                  Number(b.productCount || 0) - Number(a.productCount || 0)
                );
              }),
              title: items.map(item => item.name).join("\n") || "Sem itens"
            };
          })
        };
      });
    },

    abcMatrixFlatCells() {
      return this.abcMatrixRows.reduce((cells, row) => {
        return cells.concat(row.cells);
      }, []);
    }
  },

  methods: {
    hexToRgb(hex) {
      if (!hex) return null;
      const normalized = String(hex)
        .replace("#", "")
        .trim();
      const value =
        normalized.length === 3
          ? normalized
              .split("")
              .map(char => `${char}${char}`)
              .join("")
          : normalized;

      if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;

      return {
        r: parseInt(value.slice(0, 2), 16),
        g: parseInt(value.slice(2, 4), 16),
        b: parseInt(value.slice(4, 6), 16)
      };
    },

    dashboardPrimaryRgba(alpha) {
      const rgb = this.hexToRgb(this.dashboardPrimaryColor) || {
        r: 240,
        g: 81,
        b: 45
      };

      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    },

    getDashboardBISettings() {
      return (this.preferences && this.preferences.dashboardBI) || {};
    },

    applyDashboardBISettings() {
      const settings = this.getDashboardBISettings();
      if (!Object.keys(settings).length) return;

      this.isApplyingDashboardBISettings = true;
      this.sidebarCollapsed =
        settings.sidebarCollapsed !== undefined
          ? settings.sidebarCollapsed
          : this.sidebarCollapsed;
      this.selectedStoreIds = Array.isArray(settings.selectedStoreIds)
        ? settings.selectedStoreIds
        : this.selectedStoreIds;
      this.dashboardScopeSearch =
        settings.dashboardScopeLabel || this.dashboardScopeLabel;
      this.showMapPins =
        settings.showMapPins !== undefined
          ? settings.showMapPins
          : this.showMapPins;
      this.savedLeafletView = settings.leafletView || this.savedLeafletView;
      this.restoreLeafletViewAfterLoad = Boolean(this.savedLeafletView);
      this.comparisonDate = settings.comparisonDate
        ? this.moment(settings.comparisonDate, "YYYY-MM-DD").toDate()
        : this.comparisonDate;
      this.topRankingMode = settings.topRankingMode || this.topRankingMode;
      this.paymentChartMode =
        settings.paymentChartMode || this.paymentChartMode;
      this.productsStartDate = settings.productsStartDate
        ? this.moment(settings.productsStartDate, "YYYY-MM-DD").toDate()
        : this.productsStartDate;
      this.productsEndDate = settings.productsEndDate
        ? this.moment(settings.productsEndDate, "YYYY-MM-DD").toDate()
        : this.productsEndDate;
      this.productMetricMode =
        settings.productMetricMode || this.productMetricMode;
      this.productDimensionMode =
        settings.productDimensionMode || this.productDimensionMode;
      this.matrixDimensionMode =
        settings.matrixDimensionMode || this.matrixDimensionMode;
      this.salesComparisonEnabled =
        settings.salesComparisonEnabled !== undefined
          ? settings.salesComparisonEnabled
          : this.salesComparisonEnabled;
      this.salesComparisonStartDate = settings.salesComparisonStartDate
        ? this.moment(settings.salesComparisonStartDate, "YYYY-MM-DD").toDate()
        : this.getDefaultSalesComparisonStartDate();
      this.salesComparisonCustomStartDate = Boolean(
        settings.salesComparisonCustomStartDate
      );
      if (!this.salesComparisonCustomStartDate) {
        this.salesComparisonStartDate = this.getDefaultSalesComparisonStartDate();
      }
      this.salesDailyGroupMode = "day";
      this.salesTimeMode = settings.salesTimeMode || this.salesTimeMode;
      this.normalizeDashboardPeriodDates();
      this.$nextTick(() => {
        this.isApplyingDashboardBISettings = false;
      });
    },

    buildDashboardBISettings() {
      return {
        sidebarCollapsed: this.sidebarCollapsed,
        activeTab: this.activeTab,
        selectedStoreIds: this.selectedStoreIds,
        dashboardScopeLabel: this.dashboardScopeLabel,
        showMapPins: this.showMapPins,
        leafletView: this.getCurrentLeafletView(),
        comparisonDate: this.comparisonDate
          ? this.moment(this.comparisonDate).format("YYYY-MM-DD")
          : null,
        topRankingMode: this.topRankingMode,
        paymentChartMode: this.paymentChartMode,
        productsStartDate: this.moment(this.productsStartDate).format(
          "YYYY-MM-DD"
        ),
        productsEndDate: this.moment(this.productsEndDate).format("YYYY-MM-DD"),
        productMetricMode: this.productMetricMode,
        productDimensionMode: this.productDimensionMode,
        matrixDimensionMode: this.matrixDimensionMode,
        salesComparisonEnabled: this.salesComparisonEnabled,
        salesComparisonStartDate: this.salesComparisonStartDate
          ? this.moment(this.salesComparisonStartDate).format("YYYY-MM-DD")
          : null,
        salesComparisonCustomStartDate: this.salesComparisonCustomStartDate,
        salesDailyGroupMode: this.salesDailyGroupMode,
        salesTimeMode: this.salesTimeMode
      };
    },

    scheduleSaveDashboardBISettings() {
      if (this.isApplyingDashboardBISettings) return;
      if (this.dashboardBISettingsSaveTimeout) {
        window.clearTimeout(this.dashboardBISettingsSaveTimeout);
      }

      this.dashboardBISettingsSaveTimeout = window.setTimeout(() => {
        this.$store
          .dispatch("saveDashboardBISettings", this.buildDashboardBISettings())
          .catch(() => {});
      }, 450);
    },

    saveDashboardBISettingsNow() {
      if (this.isApplyingDashboardBISettings) return;
      if (this.dashboardBISettingsSaveTimeout) {
        window.clearTimeout(this.dashboardBISettingsSaveTimeout);
        this.dashboardBISettingsSaveTimeout = null;
      }
      this.$store
        .dispatch("saveDashboardBISettings", this.buildDashboardBISettings())
        .catch(() => {});
    },

    centsToExportValue(value) {
      return Number(value || 0) / 100;
    },

    formatExportDate(date) {
      if (!date) return "";
      return this.moment(date).format("DD/MM/YYYY");
    },

    formatExportDateString(dateString) {
      if (!dateString) return "";
      return this.moment(dateString, "YYYY-MM-DD").format("DD/MM/YYYY");
    },

    getDashboardExportPeriodLabel() {
      return `${this.formatExportDate(
        this.productsStartDate
      )} - ${this.formatExportDate(this.productsEndDate)}`;
    },

    getDashboardExportedAtLabel() {
      return this.moment().format("DD/MM/YYYY HH:mm:ss");
    },

    dashboardExportPeriodIncludesToday() {
      if (!this.productsStartDate || !this.productsEndDate) return false;

      const today = this.moment().startOf("day");
      const startDate = this.moment(this.productsStartDate).startOf("day");
      const endDate = this.moment(this.productsEndDate).startOf("day");

      return today.isBetween(startDate, endDate, null, "[]");
    },

    getDashboardExportMetadataRows() {
      const rows = [["Exportado em", this.getDashboardExportedAtLabel()]];

      if (this.dashboardExportPeriodIncludesToday()) {
        rows.push([
          "Aviso",
          "O período inclui hoje. Os dados são atualizados ao longo do dia e podem mudar entre carregamentos ou exportações."
        ]);
      }

      return rows;
    },

    getSalesComparisonExportPeriodLabel() {
      if (
        this.salesComparisonPeriod.startDate &&
        this.salesComparisonPeriod.endDate
      ) {
        return `${this.formatExportDateString(
          this.salesComparisonPeriod.startDate
        )} - ${this.formatExportDateString(
          this.salesComparisonPeriod.endDate
        )}`;
      }

      if (!this.salesComparisonStartDate) return "";

      const endDate = this.moment(this.salesComparisonStartDate)
        .add(this.salesPeriodDays - 1, "days")
        .toDate();
      return `${this.formatExportDate(
        this.salesComparisonStartDate
      )} - ${this.formatExportDate(endDate)}`;
    },

    getExportCurrencyFormat() {
      return '"R$" #,##0.00';
    },

    normalizeExportHeader(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    },

    isExportMoneyLabel(value) {
      return [
        "faturamento",
        "faturamento bruto",
        "faturamento comparacao",
        "ticket medio",
        "valor de chargebacks",
        "diferenca"
      ].includes(this.normalizeExportHeader(value));
    },

    isExportPercentageLabel(value) {
      return ["delta percentual"].includes(this.normalizeExportHeader(value));
    },

    applyCurrencyFormatToCells(worksheet, cells) {
      (cells || []).forEach(cell => {
        if (worksheet[cell]) {
          worksheet[cell].z = this.getExportCurrencyFormat();
        }
      });
    },

    applyPercentageFormatToCells(worksheet, cells) {
      (cells || []).forEach(cell => {
        if (worksheet[cell]) {
          worksheet[cell].z = "0.00%";
        }
      });
    },

    applyBoldFormatToRow(worksheet, rowIndex) {
      const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:A1");
      for (let column = range.s.c; column <= range.e.c; column += 1) {
        const cell =
          worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: column })];
        if (!cell) continue;
        cell.s = Object.assign({}, cell.s, {
          font: Object.assign({}, cell.s && cell.s.font, { bold: true })
        });
      }
    },

    applyWarningFormatToRows(worksheet) {
      const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:A1");
      for (let row = range.s.r; row <= range.e.r; row += 1) {
        const labelCell = worksheet[XLSX.utils.encode_cell({ r: row, c: 0 })];
        if (!labelCell || this.normalizeExportHeader(labelCell.v) !== "aviso") {
          continue;
        }

        for (let column = range.s.c; column <= range.e.c; column += 1) {
          const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: column })];
          if (!cell) continue;
          cell.s = Object.assign({}, cell.s, {
            fill: {
              patternType: "solid",
              fgColor: { rgb: "FFF4D6" }
            },
            font: Object.assign({}, cell.s && cell.s.font, {
              color: { rgb: "8A5A00" }
            })
          });
        }
      }
    },

    applyCurrencyFormatToWorksheet(worksheet) {
      const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:A1");
      for (let column = range.s.c; column <= range.e.c; column += 1) {
        const headerCell =
          worksheet[XLSX.utils.encode_cell({ r: range.s.r, c: column })];
        if (!headerCell) continue;
        const isMoneyColumn = this.isExportMoneyLabel(headerCell.v);
        const isPercentageColumn = this.isExportPercentageLabel(headerCell.v);
        if (!isMoneyColumn && !isPercentageColumn) continue;

        for (let row = range.s.r + 1; row <= range.e.r; row += 1) {
          const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: column })];
          if (cell && typeof cell.v === "number" && isMoneyColumn) {
            cell.z = this.getExportCurrencyFormat();
          }
          if (cell && typeof cell.v === "number" && isPercentageColumn) {
            cell.z = "0.00%";
          }
        }
      }

      for (let row = range.s.r; row <= range.e.r; row += 1) {
        const labelCell = worksheet[XLSX.utils.encode_cell({ r: row, c: 0 })];
        const valueCell = worksheet[XLSX.utils.encode_cell({ r: row, c: 1 })];
        if (
          labelCell &&
          valueCell &&
          typeof valueCell.v === "number" &&
          this.isExportMoneyLabel(labelCell.v)
        ) {
          valueCell.z = this.getExportCurrencyFormat();
        }
      }
    },

    getExportWeekdayLabel(dateString) {
      if (!dateString) return "";
      const labels = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
      return labels[this.moment(dateString, "YYYY-MM-DD").day()] || "";
    },

    getExportDayMonthLabel(dateString) {
      if (!dateString) return "";
      return this.moment(dateString, "YYYY-MM-DD").format("DD/MM");
    },

    getExportDeltaPercentage(currentValue, comparisonValue) {
      const current = Number(currentValue || 0);
      const comparison = Number(comparisonValue || 0);
      if (!comparison) return "";
      return (current - comparison) / comparison;
    },

    getSalesWeekDayRows(rows) {
      const weekDays = [
        { index: 1, label: "Seg", grossSales: 0 },
        { index: 2, label: "Ter", grossSales: 0 },
        { index: 3, label: "Qua", grossSales: 0 },
        { index: 4, label: "Qui", grossSales: 0 },
        { index: 5, label: "Sex", grossSales: 0 },
        { index: 6, label: "Sáb", grossSales: 0 },
        { index: 0, label: "Dom", grossSales: 0 }
      ];
      const byIndex = weekDays.reduce((days, day) => {
        days[day.index] = day;
        return days;
      }, {});

      (rows || []).forEach(item => {
        const weekDay = this.moment(item.dateString, "YYYY-MM-DD").day();
        if (!byIndex[weekDay]) return;
        byIndex[weekDay].grossSales += Number(item.grossSales || 0);
      });

      return weekDays;
    },

    autoFitExportWorksheetColumns(worksheet) {
      const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:A1");
      const columnWidths = [];

      for (let column = range.s.c; column <= range.e.c; column += 1) {
        let width = 12;
        for (let row = range.s.r; row <= range.e.r; row += 1) {
          const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: column })];
          if (!cell || cell.v === undefined || cell.v === null) continue;
          const formattedValue = XLSX.utils.format_cell(cell);
          const cellValue = formattedValue || String(cell.v);
          width = Math.max(width, cellValue.length + 4);
        }
        columnWidths.push({ wch: Math.min(width, 80) });
      }

      worksheet["!cols"] = columnWidths;
    },

    appendDashboardExportSheet(workbook, sheetName, rows, worksheet) {
      const exportWorksheet =
        worksheet ||
        XLSX.utils.json_to_sheet(
          rows && rows.length ? rows : [{ Vazio: "Sem dados" }]
        );
      if (!worksheet) {
        this.applyBoldFormatToRow(exportWorksheet, 0);
      }
      this.applyCurrencyFormatToWorksheet(exportWorksheet);
      this.autoFitExportWorksheetColumns(exportWorksheet);
      XLSX.utils.book_append_sheet(
        workbook,
        exportWorksheet,
        sheetName.slice(0, 31)
      );
    },

    writeDashboardExportWorkbook(fileNamePrefix, sheets) {
      const workbook = XLSX.utils.book_new();
      sheets.forEach(sheet => {
        this.appendDashboardExportSheet(
          workbook,
          sheet.name,
          sheet.rows,
          sheet.worksheet
        );
      });
      XLSX.writeFile(
        workbook,
        `${fileNamePrefix}-${this.moment().format("YYYY-MM-DD-HHmm")}.xlsx`
      );
    },

    exportSalesDashboard() {
      const comparisonPeriod = this.salesComparisonEnabled
        ? this.getSalesComparisonExportPeriodLabel()
        : "";
      const grossSalesValue = Number(this.salesSummary.grossSales || 0);
      const comparisonGrossSalesValue = Number(
        this.salesComparisonSummary.grossSales || 0
      );
      const transactionsCount = Number(
        this.salesSummary.transactionsCount || 0
      );
      const comparisonTransactionsCount = Number(
        this.salesComparisonSummary.transactionsCount || 0
      );
      const dailyRowsCount = Math.max(
        (this.salesByDay || []).length,
        (this.salesComparisonByDay || []).length
      );
      const currentDailyRows = Array.from(
        { length: dailyRowsCount },
        (_, index) => {
          const currentRow = (this.salesByDay || [])[index] || {};
          const comparisonRow = (this.salesComparisonByDay || [])[index] || {};
          const currentValue = Number(currentRow.grossSales || 0);
          const comparisonValue = Number(comparisonRow.grossSales || 0);
          return {
            "Dia do mês": this.getExportDayMonthLabel(currentRow.dateString),
            "Dia da semana": this.getExportWeekdayLabel(currentRow.dateString),
            Faturamento: this.centsToExportValue(currentValue),
            "Dia do mês comparação": this.getExportDayMonthLabel(
              comparisonRow.dateString
            ),
            "Dia da semana comparação": this.getExportWeekdayLabel(
              comparisonRow.dateString
            ),
            "Faturamento comparação": this.centsToExportValue(comparisonValue),
            Diferença: this.centsToExportValue(currentValue - comparisonValue),
            "Delta percentual": this.getExportDeltaPercentage(
              currentValue,
              comparisonValue
            )
          };
        }
      );
      const hourlyRows = Array.from({ length: 24 }, (_, hour) => {
        const currentValue = Number((this.salesByHour || [])[hour] || 0);
        const comparisonValue = Number(
          (this.salesComparisonByHour || [])[hour] || 0
        );
        return {
          Hora: `${String(hour).padStart(2, "0")}:00`,
          Faturamento: this.centsToExportValue(currentValue),
          "Faturamento comparação": this.centsToExportValue(comparisonValue),
          Diferença: this.centsToExportValue(currentValue - comparisonValue),
          "Delta percentual": this.getExportDeltaPercentage(
            currentValue,
            comparisonValue
          )
        };
      });
      const comparisonWeekDays = this.getSalesWeekDayRows(
        this.salesComparisonByDay
      );
      const weekDayRows = this.salesByWeekDay.map((row, index) => {
        const comparisonRow = comparisonWeekDays[index] || {};
        const currentValue = Number(row.grossSales || 0);
        const comparisonValue = Number(comparisonRow.grossSales || 0);
        return {
          "Dia da semana": row.label,
          Faturamento: this.centsToExportValue(currentValue),
          "Faturamento comparação": this.centsToExportValue(comparisonValue),
          Diferença: this.centsToExportValue(currentValue - comparisonValue),
          "Delta percentual": this.getExportDeltaPercentage(
            currentValue,
            comparisonValue
          )
        };
      });
      const paymentRows = [
        { mode: "channel", label: "Canal" },
        { mode: "type", label: "Tipo" },
        { mode: "breakdown", label: "Detalhado" }
      ].reduce((rows, option) => {
        return rows.concat(
          this.getPaymentChartData(this.salesSummary, option.mode, {
            includeDiscounts: true
          }).map(row => {
            return {
              Visão: option.label,
              Meio: row.label,
              Faturamento: this.centsToExportValue(row.value)
            };
          })
        );
      }, []);
      const topStoreRows = (this.salesTopStores || []).map((row, index) => {
        return {
          Ranking: index + 1,
          Loja: row.storeName || "Loja sem nome",
          Faturamento: this.centsToExportValue(row.grossSales),
          Vendas: Number(row.transactionsCount || row.productCount || 0)
        };
      });
      const salesSummarySheet = XLSX.utils.aoa_to_sheet(
        [
          ["Período", this.getDashboardExportPeriodLabel()],
          ["Faturamento bruto", this.centsToExportValue(grossSalesValue)],
          ["Vendas", transactionsCount],
          ["Ticket médio", this.centsToExportValue(this.salesAverageTicket)],
          ["Lojas ativas", Number(this.salesSummary.activeStores || 0)],
          [
            "Valor de chargebacks",
            this.centsToExportValue(this.salesSummary.chargedback)
          ],
          [
            "Quantidade de chargebacks",
            Number(this.salesSummary.chargedbackTransactionsCount || 0)
          ],
          ["Período comparação", comparisonPeriod],
          [
            "Faturamento comparação",
            this.salesComparisonEnabled
              ? this.centsToExportValue(comparisonGrossSalesValue)
              : "",
            this.salesComparisonEnabled
              ? this.centsToExportValue(
                  grossSalesValue - comparisonGrossSalesValue
                )
              : "",
            this.salesComparisonEnabled
              ? this.getExportDeltaPercentage(
                  grossSalesValue,
                  comparisonGrossSalesValue
                )
              : ""
          ],
          [
            "Vendas comparação",
            this.salesComparisonEnabled ? comparisonTransactionsCount : "",
            this.salesComparisonEnabled
              ? transactionsCount - comparisonTransactionsCount
              : "",
            this.salesComparisonEnabled
              ? this.getExportDeltaPercentage(
                  transactionsCount,
                  comparisonTransactionsCount
                )
              : ""
          ]
        ].concat(this.getDashboardExportMetadataRows())
      );
      this.applyCurrencyFormatToCells(salesSummarySheet, [
        "B2",
        "B4",
        "B6",
        "B9",
        "C9"
      ]);
      this.applyPercentageFormatToCells(salesSummarySheet, ["D9", "D10"]);
      this.applyWarningFormatToRows(salesSummarySheet);

      this.writeDashboardExportWorkbook("dashboard-vendas-merchant", [
        {
          name: "Resumo",
          worksheet: salesSummarySheet
        },
        { name: "Vendas no período", rows: currentDailyRows },
        { name: "Vendas por hora", rows: hourlyRows },
        { name: "Vendas por dia semana", rows: weekDayRows },
        { name: "Meios pagamento", rows: paymentRows },
        { name: "Top lojas", rows: topStoreRows }
      ]);
    },

    exportProductsDashboard() {
      const dimensionOption =
        this.productDimensionModes.find(mode => {
          return mode.key === this.productDimensionMode;
        }) || {};
      const metricOption =
        this.productMetricModes.find(mode => {
          return mode.key === this.productMetricMode;
        }) || {};
      const matrixDimensionOption =
        this.productDimensionModes.find(mode => {
          return mode.key === this.matrixDimensionMode;
        }) || {};
      const dimensionLabel = dimensionOption.label || this.productDimensionMode;
      const metricLabel = metricOption.label || this.productMetricMode;
      const matrixDimensionLabel =
        matrixDimensionOption.label || this.matrixDimensionMode;
      const getDimensionLabel = dimension => {
        const option =
          this.productDimensionModes.find(mode => mode.key === dimension) || {};
        return option.label || dimension;
      };
      const buildGroupedRows = dimension => {
        const dimensionName = getDimensionLabel(dimension);
        return this.getGroupedProductRows(dimension)
          .sort((a, b) => Number(b.grossSales || 0) - Number(a.grossSales || 0))
          .map((row, index) => {
            return {
              Ranking: index + 1,
              Dimensão: dimensionName,
              Item: row.name,
              Quantidade: Number(row.productCount || 0),
              Faturamento: this.centsToExportValue(row.grossSales),
              SKUs: Number(row.skusSold || 0)
            };
          });
      };
      const buildAbcRows = dimension => {
        const dimensionName = getDimensionLabel(dimension);
        const rows = this.getGroupedProductRows(dimension).sort((a, b) => {
          return (
            this.getProductMetricValue(b, this.productMetricMode) -
            this.getProductMetricValue(a, this.productMetricMode)
          );
        });

        return this.getProductsAbcRows(rows, this.productMetricMode).map(
          (row, index) => {
            return {
              Ranking: index + 1,
              Dimensão: dimensionName,
              Métrica: metricLabel,
              Item: row.name,
              "Classe ABC": row.abcClass,
              Quantidade: Number(row.productCount || 0),
              Faturamento: this.centsToExportValue(row.grossSales),
              SKUs: Number(row.skusSold || 0),
              "Percentual representatividade": row.individualPercentageLabel,
              "Percentual acumulado": row.cumulativePercentageLabel
            };
          }
        );
      };
      const productDimensionRows = buildGroupedRows("product");
      const categoryDimensionRows = buildGroupedRows("category");
      const subcategoryDimensionRows = buildGroupedRows("subcategory");
      const productAbcRows = buildAbcRows("product");
      const categoryAbcRows = buildAbcRows("category");
      const subcategoryAbcRows = buildAbcRows("subcategory");
      const abcRowsByActiveDimension = {
        product: productAbcRows,
        category: categoryAbcRows,
        subcategory: subcategoryAbcRows
      };
      const abcSummaryRows = this.abcSummary.map(item => {
        return {
          Classe: item.key,
          Itens: Number(item.count || 0)
        };
      });
      const matrixSummaryRows = this.abcMatrixFlatCells.map(cell => {
        return {
          Célula: cell.key,
          Dimensão: matrixDimensionLabel,
          Prioridade: cell.priority,
          Itens: Number(cell.count || 0),
          Percentual: cell.percentage,
          Descrição: cell.description
        };
      });
      const matrixItemRows = this.abcMatrixFlatCells.reduce((rows, cell) => {
        return rows.concat(
          cell.items.map(item => {
            return {
              Célula: cell.key,
              Dimensão: matrixDimensionLabel,
              Item: item.name,
              "Classe quantidade": cell.key.charAt(0),
              "Classe faturamento": cell.key.charAt(1),
              Quantidade: Number(item.productCount || 0),
              Faturamento: this.centsToExportValue(item.grossSales),
              SKUs: Number(item.skusSold || 0)
            };
          })
        );
      }, []);
      const rawProductRows = (this.productRows || []).map(row => {
        return {
          Produto: row.productName || "Produto sem nome",
          Categoria: this.categoryNameById[row.category] || "Sem categoria",
          Subcategoria:
            this.subcategoryNameById[row.subcategory] || "Sem subcategoria",
          Quantidade: Number(row.productCount || 0),
          Faturamento: this.centsToExportValue(row.grossSales)
        };
      });
      const productsSummarySheet = XLSX.utils.aoa_to_sheet(
        [
          ["Período", this.getDashboardExportPeriodLabel()],
          ["Itens vendidos", Number(this.productsSummary.itemsSold || 0)],
          ["SKUs vendidos", Number(this.productsSummary.skusSold || 0)],
          [
            "Faturamento",
            this.centsToExportValue(this.productsSummary.grossSales)
          ],
          ["Dimensão curva ABC", dimensionLabel],
          ["Métrica curva ABC", metricLabel],
          ["Dimensão matriz ABC", matrixDimensionLabel],
          ["Confiabilidade matriz ABC", this.abcMatrixReliability.label],
          ["Detalhe confiabilidade", this.abcMatrixReliability.tooltip]
        ].concat(this.getDashboardExportMetadataRows())
      );
      this.applyCurrencyFormatToCells(productsSummarySheet, ["B4"]);
      this.applyWarningFormatToRows(productsSummarySheet);

      this.writeDashboardExportWorkbook("dashboard-produtos-merchant", [
        {
          name: "Resumo",
          worksheet: productsSummarySheet
        },
        { name: "Resumo ABC", rows: abcSummaryRows },
        {
          name: "Curva ABC",
          rows: abcRowsByActiveDimension[this.productDimensionMode] || []
        },
        { name: "Produtos agrupados", rows: productDimensionRows },
        { name: "Categorias", rows: categoryDimensionRows },
        { name: "Subcategorias", rows: subcategoryDimensionRows },
        { name: "ABC Produtos", rows: productAbcRows },
        { name: "ABC Categorias", rows: categoryAbcRows },
        { name: "ABC Subcategorias", rows: subcategoryAbcRows },
        { name: "Matriz ABC", rows: matrixSummaryRows },
        { name: "Itens matriz ABC", rows: matrixItemRows },
        { name: "Produtos", rows: rawProductRows }
      ]);
    },

    getCurrentLeafletView() {
      if (!this.map) return this.savedLeafletView;
      const center = this.map.getCenter();
      return {
        lat: center.lat,
        lng: center.lng,
        zoom: this.map.getZoom()
      };
    },

    getTodayDashboard() {
      this.isLoading = true;
      this.$store
        .dispatch("getDashboardToday", this.dashboardScopeParams)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
            return;
          }

          const payload = res.data.payload || {};
          this.summary = Object.assign({}, this.summary, payload.summary || {});
          this.topStores = payload.topStores || [];
          this.topProducts = payload.topProducts || [];
          this.topCategories = payload.topCategories || [];
          this.topSubcategories = payload.topSubcategories || [];
          this.activeStoreIds = payload.activeStoreIds || [];
          this.mapStores = payload.stores || [];
          this.recentTransactions = (payload.recentTransactions || []).map(
            this.normalizeTransaction
          );
          this.renderCharts();
          this.$nextTick(this.renderMap);
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getProductsDashboard() {
      this.isLoading = true;
      this.$store
        .dispatch("getDashboardProducts", this.productsDashboardParams)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data.message);
            return;
          }

          const payload = res.data.payload || {};
          this.productsSummary = Object.assign(
            {},
            this.productsSummary,
            payload.summary || {}
          );
          this.productRows = payload.products || [];
          this.renderProductCharts();
        })
        .catch(() => {
          this.alertError("UNEXPECTED_ERROR");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getSalesDashboard() {
      this.isLoading = true;
      this.$store
        .dispatch("getDashboardSales", this.salesDashboardParams)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data.message);
            return;
          }

          const payload = res.data.payload || {};
          this.salesSummary = Object.assign(
            {},
            this.salesSummary,
            payload.summary || {}
          );
          this.salesComparisonSummary = Object.assign(
            {},
            this.salesComparisonSummary,
            payload.comparisonSummary || {}
          );
          this.salesByDay = payload.salesByDay || [];
          this.salesComparisonByDay = payload.comparisonSalesByDay || [];
          this.salesByHour = payload.salesByHour || [];
          this.salesComparisonByHour = payload.comparisonSalesByHour || [];
          this.salesTopStores = payload.topStores || [];
          this.salesComparisonPeriod = {
            startDate: payload.comparisonStartDate || null,
            endDate: payload.comparisonEndDate || null
          };
          this.renderSalesCharts();
        })
        .catch(() => {
          this.alertError("UNEXPECTED_ERROR");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getActiveDashboardData() {
      if (this.activeTab === "sales") {
        this.getSalesDashboard();
        return;
      }
      if (this.activeTab === "products") {
        this.getProductsDashboard();
        return;
      }
      this.getTodayDashboard();
    },

    handleDashboardPeriodChange() {
      this.normalizeDashboardPeriodDates();
      if (
        this.productsStartDate &&
        this.productsEndDate &&
        this.moment(this.productsStartDate).isAfter(this.productsEndDate)
      ) {
        this.productsEndDate = this.productsStartDate;
      }

      if (this.activeTab === "sales" && !this.salesComparisonCustomStartDate) {
        this.salesComparisonStartDate = this.getDefaultSalesComparisonStartDate();
      }
      this.normalizeSalesComparisonStartDate();
    },

    normalizeDashboardPeriodDates() {
      const today = this.moment().endOf("day");
      if (
        this.productsStartDate &&
        this.moment(this.productsStartDate).isAfter(today)
      ) {
        this.productsStartDate = today.toDate();
      }
      if (
        this.productsEndDate &&
        this.moment(this.productsEndDate).isAfter(today)
      ) {
        this.productsEndDate = today.toDate();
      }
    },

    getDefaultSalesComparisonStartDate() {
      const currentStartDate = this.moment(this.productsStartDate).startOf(
        "day"
      );
      const currentEndDate = this.moment(this.productsEndDate).startOf("day");
      const comparisonEndDate = currentStartDate.clone().subtract(1, "day");

      while (comparisonEndDate.day() !== currentEndDate.day()) {
        comparisonEndDate.subtract(1, "day");
      }

      return comparisonEndDate
        .subtract(this.salesPeriodDays - 1, "days")
        .toDate();
    },

    normalizeSalesComparisonStartDate() {
      if (!this.salesComparisonStartDate) {
        this.salesComparisonStartDate = this.getDefaultSalesComparisonStartDate();
        return;
      }

      if (
        this.moment(this.salesComparisonStartDate).isAfter(
          this.maxSalesComparisonStartDate
        )
      ) {
        this.salesComparisonStartDate = this.maxSalesComparisonStartDate;
      }
    },

    toggleSalesComparison() {
      this.salesComparisonEnabled = !this.salesComparisonEnabled;
      if (this.salesComparisonEnabled && !this.salesComparisonStartDate) {
        this.salesComparisonStartDate = this.getDefaultSalesComparisonStartDate();
      }
    },

    handleSalesComparisonStartDateChange() {
      this.salesComparisonCustomStartDate = true;
      this.clearSalesComparisonPreview();
      this.normalizeSalesComparisonStartDate();
      this.syncSalesComparisonInputDisplay();
    },

    formatSalesComparisonInputDate(date) {
      if (!date) return "";
      const startDate = this.moment(date);
      const endDate = startDate.clone().add(this.salesPeriodDays - 1, "days");
      return `${startDate.format("DD/MM")} - ${endDate.format("DD/MM")}`;
    },

    syncSalesComparisonInputDisplay() {
      this.$nextTick(() => {
        const picker = this.$refs.salesComparisonDatepicker;
        const input = picker && picker.$refs && picker.$refs.input;
        if (!input) return;
        input.newValue = this.salesComparisonDisplayedPeriodLabel;
      });
    },

    handleSalesComparisonDatepickerActiveChange(isActive) {
      if (isActive) {
        this.$nextTick(this.attachSalesComparisonPreviewHandlers);
        return;
      }

      this.clearSalesComparisonPreview();
      this.detachSalesComparisonPreviewHandlers();
    },

    attachSalesComparisonPreviewHandlers() {
      const picker = this.$refs.salesComparisonDatepicker;
      const root = picker && picker.$el;
      if (!root || this.salesComparisonDatepickerMouseoverHandler) return;

      this.salesComparisonDatepickerMouseoverHandler = event => {
        const cell = event.target.closest(".datepicker-cell.is-selectable");
        if (
          !cell ||
          !root.contains(cell) ||
          cell.classList.contains("is-invisible")
        ) {
          return;
        }

        const dayNode = cell.querySelector("span");
        const day = Number(dayNode ? dayNode.textContent : "");
        const focused = picker.focusedDateData || {};
        if (!day || focused.month === undefined || focused.year === undefined) {
          return;
        }

        const startDate = new Date(focused.year, focused.month, day);
        if (this.moment(startDate).isAfter(this.maxSalesComparisonStartDate)) {
          this.clearSalesComparisonPreview();
          return;
        }

        this.salesComparisonPreviewStartDate = startDate;
        this.syncSalesComparisonInputDisplay();
        this.applySalesComparisonPreviewClasses();
      };
      this.salesComparisonDatepickerMouseleaveHandler = () => {
        this.clearSalesComparisonPreview();
      };

      root.addEventListener(
        "mouseover",
        this.salesComparisonDatepickerMouseoverHandler
      );
      root.addEventListener(
        "mouseleave",
        this.salesComparisonDatepickerMouseleaveHandler
      );
    },

    detachSalesComparisonPreviewHandlers() {
      const picker = this.$refs.salesComparisonDatepicker;
      const root = picker && picker.$el;
      if (!root) return;

      if (this.salesComparisonDatepickerMouseoverHandler) {
        root.removeEventListener(
          "mouseover",
          this.salesComparisonDatepickerMouseoverHandler
        );
        this.salesComparisonDatepickerMouseoverHandler = null;
      }
      if (this.salesComparisonDatepickerMouseleaveHandler) {
        root.removeEventListener(
          "mouseleave",
          this.salesComparisonDatepickerMouseleaveHandler
        );
        this.salesComparisonDatepickerMouseleaveHandler = null;
      }
    },

    clearSalesComparisonPreview() {
      this.salesComparisonPreviewStartDate = null;
      this.clearSalesComparisonPreviewClasses();
      this.syncSalesComparisonInputDisplay();
    },

    clearSalesComparisonPreviewClasses() {
      const picker = this.$refs.salesComparisonDatepicker;
      const root = picker && picker.$el;
      if (!root) return;

      root
        .querySelectorAll(
          ".sales-comparison-preview, .sales-comparison-preview-start, .sales-comparison-preview-end"
        )
        .forEach(cell => {
          cell.classList.remove(
            "sales-comparison-preview",
            "sales-comparison-preview-start",
            "sales-comparison-preview-end"
          );
        });
    },

    applySalesComparisonPreviewClasses() {
      const picker = this.$refs.salesComparisonDatepicker;
      const root = picker && picker.$el;
      if (!root || !this.salesComparisonPreviewStartDate) return;

      this.clearSalesComparisonPreviewClasses();
      const focused = picker.focusedDateData || {};
      const startDate = this.moment(
        this.salesComparisonPreviewStartDate
      ).startOf("day");
      const endDate = startDate
        .clone()
        .add(this.salesPeriodDays - 1, "days")
        .startOf("day");

      root
        .querySelectorAll(".datepicker-cell:not(.is-invisible)")
        .forEach(cell => {
          const dayNode = cell.querySelector("span");
          const day = Number(dayNode ? dayNode.textContent : "");
          if (!day) return;

          const date = this.moment(
            new Date(focused.year, focused.month, day)
          ).startOf("day");
          if (date.isBefore(startDate) || date.isAfter(endDate)) return;

          cell.classList.add("sales-comparison-preview");
          if (date.isSame(startDate, "day")) {
            cell.classList.add("sales-comparison-preview-start");
          }
          if (date.isSame(endDate, "day")) {
            cell.classList.add("sales-comparison-preview-end");
          }
        });
    },

    openDashboardScopeDropdown() {
      this.dashboardScopeSearchDirty = false;
      this.dashboardScopeDropdownOpen = true;
      this.dashboardScopeSearch = "";
      this.dashboardScopeHighlightedIndex = Math.max(
        this.dashboardScopeOptions.findIndex(option => {
          return this.isDashboardScopeOptionSelected(option);
        }),
        0
      );
    },

    handleDashboardScopeInput() {
      this.dashboardScopeSearchDirty = true;
      this.dashboardScopeDropdownOpen = true;
      this.dashboardScopeHighlightedIndex = 0;
    },

    closeDashboardScopeDropdown() {
      window.setTimeout(() => {
        this.dashboardScopeSearch = this.dashboardScopeLabel;
        this.dashboardScopeDropdownOpen = false;
        this.dashboardScopeSearchDirty = false;
      }, 120);
    },

    toggleDashboardScopeOption(option) {
      if (option.value === "all") {
        this.selectedStoreIds = [];
      } else {
        const storeId = option.value.replace("store:", "");
        if (this.selectedStoreIds.includes(storeId)) {
          this.selectedStoreIds = this.selectedStoreIds.filter(id => {
            return id !== storeId;
          });
        } else {
          this.selectedStoreIds = this.selectedStoreIds.concat([storeId]);
        }
      }

      this.dashboardScopeSearch = this.dashboardScopeLabel;
      this.dashboardScopeSearchDirty = false;
      if (!["sales", "products"].includes(this.activeTab)) {
        this.getActiveDashboardData();
      }
    },

    isDashboardScopeOptionSelected(option) {
      if (option.value === "all") return this.selectedStoreIds.length === 0;
      return this.selectedStoreIds.includes(option.value.replace("store:", ""));
    },

    moveDashboardScopeHighlight(direction) {
      if (!this.dashboardScopeDropdownOpen) {
        this.openDashboardScopeDropdown();
        return;
      }

      const total = this.filteredDashboardScopeOptions.length;
      if (!total) return;

      this.dashboardScopeHighlightedIndex =
        (this.dashboardScopeHighlightedIndex + direction + total) % total;
    },

    selectHighlightedDashboardScope() {
      const option = this.filteredDashboardScopeOptions[
        this.dashboardScopeHighlightedIndex
      ];
      if (option) this.toggleDashboardScopeOption(option);
    },

    sumSalesByHourUntil(values, hour) {
      return (values || []).slice(0, hour + 1).reduce((total, value) => {
        return total + Number(value || 0);
      }, 0);
    },

    renderCharts() {
      if (this.activeTab !== "realtime-transactions") return;

      this.$nextTick(() => {
        this.renderHourlyChart();
        this.renderPaymentChart();
      });
    },

    destroyRealtimeCharts() {
      if (this.hourlyChart) {
        this.hourlyChart.destroy();
        this.hourlyChart = null;
      }
      if (this.paymentChart) {
        this.paymentChart.destroy();
        this.paymentChart = null;
      }
    },

    destroyMap() {
      if (!this.map) return;
      this.saveLeafletView();
      this.restoreLeafletViewAfterLoad = true;
      this.map.off("moveend zoomend", this.saveLeafletView);
      this.map.remove();
      this.map = null;
      this.storeMarkersLayer = null;
      this.pulseLayer = null;
    },

    destroyProductCharts() {
      if (this.topProductsChart) {
        this.topProductsChart.destroy();
        this.topProductsChart = null;
      }
    },

    destroySalesCharts() {
      if (this.salesDailyChart) {
        this.salesDailyChart.destroy();
        this.salesDailyChart = null;
      }
      if (this.salesHourlyChart) {
        this.salesHourlyChart.destroy();
        this.salesHourlyChart = null;
      }
      if (this.salesPaymentChart) {
        this.salesPaymentChart.destroy();
        this.salesPaymentChart = null;
      }
      if (this.salesTopStoresChart) {
        this.salesTopStoresChart.destroy();
        this.salesTopStoresChart = null;
      }
    },

    renderSalesCharts() {
      if (this.activeTab !== "sales") return;

      this.$nextTick(() => {
        this.renderSalesDailyChart();
        this.renderSalesHourlyChart();
        this.renderSalesPaymentChart();
        this.renderSalesTopStoresChart();
      });
    },

    renderSalesDailyChart() {
      if (!this.$refs.salesDailyChart) return;
      if (this.salesDailyChart) this.salesDailyChart.destroy();

      const currentBuckets = this.getGroupedDailySalesForChart(
        this.salesByDay,
        this.productsStartDate,
        this.productsEndDate
      );
      const labels = currentBuckets.map(item => item.label);
      const currentValues = currentBuckets.map(item => item.grossSales / 100);
      const currentBucketIndex = this.getCurrentSalesDailyBucketIndex(
        currentBuckets
      );
      const consolidatedValues = currentValues.map((value, index) => {
        return index >= currentBucketIndex && currentBucketIndex >= 0
          ? null
          : value;
      });
      const partialValues = currentValues.map((value, index) => {
        if (currentBucketIndex < 0) return null;
        if (index === currentBucketIndex - 1) return currentValues[index];
        if (index === currentBucketIndex) return value;
        return null;
      });
      const comparisonValues = this.salesComparisonEnabled
        ? this.getGroupedDailySalesForChart(
            this.salesComparisonByDay,
            this.salesComparisonStartDate,
            this.moment(this.salesComparisonStartDate)
              .add(this.salesPeriodDays - 1, "days")
              .toDate()
          ).map(item => item.grossSales / 100)
        : [];
      const options = this.chartOptions("R$");
      options.legend = {
        display: this.salesComparisonEnabled,
        labels: {
          boxWidth: 10,
          fontSize: 10
        }
      };
      const datasets = [
        {
          label: "Período atual consolidado",
          data: consolidatedValues,
          borderColor: this.dashboardPrimaryColor,
          backgroundColor: this.dashboardPrimaryRgba(0.12),
          pointBackgroundColor: this.dashboardPrimaryColor,
          pointRadius: 2,
          lineTension: 0.25,
          spanGaps: false
        },
        {
          label: "Período atual em construção",
          data: partialValues,
          borderColor: this.dashboardPrimaryColor,
          backgroundColor: this.dashboardPrimaryRgba(0),
          borderDash: [6, 5],
          pointBackgroundColor: "#ffffff",
          pointBorderColor: this.dashboardPrimaryColor,
          pointBorderWidth: 2,
          pointRadius: 4,
          lineTension: 0.25,
          spanGaps: false
        }
      ];

      if (this.salesComparisonEnabled) {
        datasets.push({
          label: "Período anterior",
          data: comparisonValues,
          borderColor: "#344054",
          backgroundColor: "rgba(52, 64, 84, 0)",
          borderDash: [4, 4],
          pointBackgroundColor: "#344054",
          pointRadius: 2,
          lineTension: 0.25
        });
      }

      this.salesDailyChart = new Chart(this.$refs.salesDailyChart, {
        type: "line",
        data: {
          labels,
          datasets
        },
        options
      });
    },

    isSalesDailyGroupModeAvailable(mode) {
      return this.visibleSalesDailyGroupModes.some(item => item.key === mode);
    },

    ensureValidSalesDailyGroupMode() {
      if (this.isSalesDailyGroupModeAvailable(this.salesDailyGroupMode)) return;
      this.salesDailyGroupMode = null;
    },

    getGroupedDailySalesForChart(rows, startDate, endDate) {
      const mode = this.effectiveSalesDailyGroupMode;
      const periodStart = this.moment(startDate).startOf("day");
      const periodEnd = this.moment(endDate).startOf("day");
      const buckets = {};

      (rows || []).forEach(item => {
        const date = this.moment(item.dateString, "YYYY-MM-DD");
        const bucket = this.getDailySalesBucket(
          date,
          periodStart,
          periodEnd,
          mode
        );
        if (!buckets[bucket.key]) {
          buckets[bucket.key] = {
            key: bucket.key,
            label: bucket.label,
            grossSales: 0
          };
        }
        buckets[bucket.key].grossSales += Number(item.grossSales || 0);
      });

      return Object.values(buckets).sort((a, b) => a.key.localeCompare(b.key));
    },

    getDailySalesBucket(date, periodStart, periodEnd, mode) {
      if (mode === "month") {
        const monthStart = date.clone().startOf("month");
        const monthEnd = date.clone().endOf("month");
        const start = monthStart.isBefore(periodStart)
          ? periodStart
          : monthStart;
        const end = monthEnd.isAfter(periodEnd) ? periodEnd : monthEnd;
        return {
          key: start.format("YYYY-MM"),
          label: `${start.format("DD/MM")} - ${end.format("DD/MM")}`
        };
      }

      if (mode === "week") {
        const weekStart = date.clone().startOf("isoWeek");
        const weekEnd = date.clone().endOf("isoWeek");
        const start = weekStart.isBefore(periodStart) ? periodStart : weekStart;
        const end = weekEnd.isAfter(periodEnd) ? periodEnd : weekEnd;
        return {
          key: start.format("YYYY-MM-DD"),
          label: `${start.format("DD/MM")} - ${end.format("DD/MM")}`
        };
      }

      return {
        key: date.format("YYYY-MM-DD"),
        label: date.format("DD/MM")
      };
    },

    getCurrentSalesDailyBucketIndex(buckets) {
      if (!this.productsStartDate || !this.productsEndDate) return -1;

      const today = this.moment().startOf("day");
      const periodStart = this.moment(this.productsStartDate).startOf("day");
      const periodEnd = this.moment(this.productsEndDate).startOf("day");
      if (today.isBefore(periodStart) || today.isAfter(periodEnd)) return -1;

      const currentBucket = this.getDailySalesBucket(
        today,
        periodStart,
        periodEnd,
        this.effectiveSalesDailyGroupMode
      );
      return (buckets || []).findIndex(bucket => {
        return bucket.key === currentBucket.key;
      });
    },

    renderSalesHourlyChart() {
      if (!this.$refs.salesHourlyChart) return;
      if (this.salesHourlyChart) this.salesHourlyChart.destroy();

      const labels =
        this.salesTimeMode === "weekDay"
          ? this.salesByWeekDay.map(item => item.label)
          : Array.from({ length: 24 }, (_, i) => `${i}h`);
      const values =
        this.salesTimeMode === "weekDay"
          ? this.salesByWeekDay.map(item => Number(item.grossSales || 0) / 100)
          : labels.map((_, i) => {
              return Number((this.salesByHour || [])[i] || 0) / 100;
            });

      this.salesHourlyChart = new Chart(this.$refs.salesHourlyChart, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Faturamento",
              data: values,
              backgroundColor: this.dashboardPrimaryColor
            }
          ]
        },
        options: this.chartOptions("R$")
      });
    },

    renderSalesPaymentChart() {
      if (!this.$refs.salesPaymentChart) return;
      if (this.salesPaymentChart) this.salesPaymentChart.destroy();

      const paymentData = this.getPaymentChartData(this.salesSummary, null, {
        includeDiscounts: true
      });
      const labels = paymentData.map(item => item.label);
      const values = paymentData.map(item => item.value / 100);
      const colors = paymentData.map(item => this.getPaymentColor(item.key));
      const total = this.getPaymentTotalForChart(paymentData);

      this.salesPaymentChart = new Chart(this.$refs.salesPaymentChart, {
        type: "bar",
        data: {
          labels: labels.map(this.paymentLabel),
          datasets: [
            {
              label: "Total",
              data: values,
              backgroundColor: colors,
              percentages: paymentData.map(item => {
                if (!total) return "";
                return `${((item.value / total) * 100).toFixed(1)}%`;
              })
            }
          ]
        },
        options: this.paymentChartOptions(),
        plugins: [this.paymentPercentagePlugin()]
      });
    },

    renderSalesTopStoresChart() {
      if (this.salesTopStoresChart) this.salesTopStoresChart.destroy();
      if (!this.$refs.salesTopStoresChart) {
        this.salesTopStoresChart = null;
        return;
      }

      const stores = this.salesTopStores || [];
      const total = stores.reduce((sum, store) => {
        return sum + Number(store.grossSales || 0);
      }, 0);

      this.salesTopStoresChart = new Chart(this.$refs.salesTopStoresChart, {
        type: "doughnut",
        data: {
          labels: stores.map(store => store.storeName || "Loja sem nome"),
          datasets: [
            {
              label: "Faturamento",
              data: stores.map(store => Number(store.grossSales || 0) / 100),
              backgroundColor: stores.map((_, index) => {
                return TOP_STORES_CHART_COLORS[
                  index % TOP_STORES_CHART_COLORS.length
                ];
              }),
              rawValues: stores.map(store => Number(store.grossSales || 0))
            }
          ]
        },
        options: this.salesTopStoresChartOptions(total)
      });
    },

    renderHourlyChart() {
      if (!this.$refs.hourlyChart) return;
      if (this.hourlyChart) this.hourlyChart.destroy();

      const labels = Array.from({ length: 24 }, (_, i) => `${i}h`);
      const currentHour = Number(this.moment().format("H"));
      const values = labels.map((_, i) => {
        if (i > currentHour) return null;
        return Number((this.summary.salesByHour || [])[i] || 0) / 100;
      });
      const consolidatedValues = values.map((value, i) => {
        return i >= currentHour ? null : value;
      });
      const currentHourValues = values.map((value, i) => {
        if (i === currentHour - 1) return values[i];
        if (i === currentHour) return value;
        return null;
      });
      const comparisonValues = (this.summary.comparisonSalesByHour || []).map(
        value => Number(value || 0) / 100
      );
      const datasets = [
        {
          label: "Hoje consolidado",
          data: consolidatedValues,
          borderColor: this.dashboardPrimaryColor,
          backgroundColor: this.dashboardPrimaryRgba(0.12),
          pointBackgroundColor: this.dashboardPrimaryColor,
          pointRadius: 2,
          lineTension: 0.25,
          spanGaps: false
        },
        {
          label: "Hora atual",
          data: currentHourValues,
          borderColor: this.dashboardPrimaryColor,
          backgroundColor: this.dashboardPrimaryRgba(0),
          borderDash: [6, 5],
          pointBackgroundColor: "#ffffff",
          pointBorderColor: this.dashboardPrimaryColor,
          pointBorderWidth: 2,
          pointRadius: 4,
          lineTension: 0.25,
          spanGaps: false
        }
      ];

      if (this.comparisonDate && comparisonValues.length) {
        datasets.push({
          label: this.comparisonDateLabel,
          data: comparisonValues,
          borderColor: "#344054",
          backgroundColor: "rgba(52, 64, 84, 0)",
          borderDash: [3, 4],
          pointBackgroundColor: "#344054",
          pointRadius: 2,
          lineTension: 0.25,
          spanGaps: false
        });
      }
      const options = this.chartOptions("R$");
      options.legend = {
        display: Boolean(this.comparisonDate),
        labels: {
          boxWidth: 10,
          fontSize: 10
        }
      };

      this.hourlyChart = new Chart(this.$refs.hourlyChart, {
        type: "line",
        data: {
          labels,
          datasets
        },
        options
      });
    },

    renderPaymentChart() {
      if (!this.$refs.paymentChart) return;
      if (this.paymentChart) this.paymentChart.destroy();

      const paymentData = this.getPaymentChartData(this.summary, null, {
        includeDiscounts: true
      });
      const labels = paymentData.map(item => item.label);
      const values = paymentData.map(item => item.value / 100);
      const colors = paymentData.map(item => this.getPaymentColor(item.key));
      const total = this.getPaymentTotalForChart(paymentData);

      this.paymentChart = new Chart(this.$refs.paymentChart, {
        type: "bar",
        data: {
          labels: labels.map(this.paymentLabel),
          datasets: [
            {
              label: "Total",
              data: values,
              backgroundColor: colors,
              percentages: paymentData.map(item => {
                if (!total) return "";
                return `${((item.value / total) * 100).toFixed(1)}%`;
              })
            }
          ]
        },
        options: this.paymentChartOptions(),
        plugins: [this.paymentPercentagePlugin()]
      });
    },

    setPaymentChartMode(mode) {
      if (this.paymentChartMode === mode) return;
      this.paymentChartMode = mode;
      this.renderPaymentChart();
      this.renderSalesPaymentChart();
    },

    getPaymentChartData(summary, mode, options) {
      const paymentSummary = summary || this.summary;
      const payments = paymentSummary.payments || {};
      const groups = this.getPaymentGroups(mode);
      const includeDiscounts = options && options.includeDiscounts;

      const data = groups
        .map(group => {
          return {
            key: group.key,
            label: group.label || this.paymentLabel(group.key),
            value: this.sumPaymentKeys(payments, group.keys)
          };
        })
        .filter(item => item.value);

      const discounts = Number(paymentSummary.discounts || 0);
      if (includeDiscounts && discounts > 0) {
        data.push({
          key: "discounts",
          label: "Descontos",
          value: discounts
        });
      }

      const paymentTotal = this.getPaymentTotalForChart(
        data.filter(item => item.key !== "discounts")
      );
      const expectedPaymentTotal =
        Number(paymentSummary.netSales || 0) || paymentTotal;
      const missingValue = expectedPaymentTotal - paymentTotal;
      if (missingValue > 0) {
        data.push({ key: "other", label: "Outros", value: missingValue });
      }

      return data.sort((a, b) => Number(b.value) - Number(a.value));
    },

    getPaymentGroups(mode) {
      const chartMode = mode || this.paymentChartMode;
      if (chartMode === "type") {
        return [
          {
            key: "credit",
            label: "Crédito",
            keys: [
              "terminalCredit",
              "tefCredit",
              "applePay",
              "applePayCredit",
              "googlePay",
              "googlePayCredit",
              "appCredit"
            ]
          },
          {
            key: "debit",
            label: "Débito",
            keys: [
              "terminalDebit",
              "tefDebit",
              "applePayDebit",
              "googlePayDebit"
            ]
          },
          {
            key: "pix",
            label: "Pix",
            keys: ["pix", "pixCelcoin", "pixPagbank", "pixAdyen"]
          },
          { key: "prepaid", label: "Pré-pago", keys: ["prepaid"] },
          { key: "voucher", label: "Voucher", keys: VOUCHER_PAYMENT_KEYS }
        ];
      }

      if (chartMode === "breakdown") {
        return [
          { key: "posCredit", label: "POS crédito", keys: ["terminalCredit"] },
          { key: "posDebit", label: "POS débito", keys: ["terminalDebit"] },
          { key: "tefCredit", label: "TEF crédito", keys: ["tefCredit"] },
          { key: "tefDebit", label: "TEF débito", keys: ["tefDebit"] },
          {
            key: "applePayCredit",
            label: "Apple Pay crédito",
            keys: ["applePay", "applePayCredit"]
          },
          {
            key: "applePayDebit",
            label: "Apple Pay débito",
            keys: ["applePayDebit"]
          },
          {
            key: "googlePayCredit",
            label: "Google Pay crédito",
            keys: ["googlePay", "googlePayCredit"]
          },
          {
            key: "googlePayDebit",
            label: "Google Pay débito",
            keys: ["googlePayDebit"]
          },
          { key: "pixCelcoin", label: "Pix Celcoin", keys: ["pixCelcoin"] },
          { key: "pixPagbank", label: "Pix PagBank", keys: ["pixPagbank"] },
          { key: "pixAdyen", label: "Pix Adyen", keys: ["pix", "pixAdyen"] },
          { key: "prepaid", label: "Pré-pago", keys: ["prepaid"] },
          { key: "appCredit", label: "Crédito App", keys: ["appCredit"] },
          { key: "voucher", label: "Voucher", keys: VOUCHER_PAYMENT_KEYS }
        ];
      }

      return [
        { key: "pos", label: "POS", keys: ["terminalCredit", "terminalDebit"] },
        { key: "tef", label: "TEF", keys: ["tef", "tefCredit", "tefDebit"] },
        {
          key: "applePay",
          label: "Apple Pay",
          keys: ["applePay", "applePayCredit", "applePayDebit"]
        },
        {
          key: "googlePay",
          label: "Google Pay",
          keys: ["googlePay", "googlePayCredit", "googlePayDebit"]
        },
        {
          key: "pix",
          label: "Pix",
          keys: ["pix", "pixCelcoin", "pixPagbank", "pixAdyen"]
        },
        { key: "appCredit", label: "Crédito App", keys: ["appCredit"] },
        { key: "prepaid", label: "Pré-pago", keys: ["prepaid"] },
        { key: "voucher", label: "Voucher", keys: VOUCHER_PAYMENT_KEYS }
      ];
    },

    sumPaymentKeys(payments, keys) {
      return keys.reduce((total, key) => {
        return total + Number(payments[key] || 0);
      }, 0);
    },

    getPaymentTotalForChart(paymentData) {
      return paymentData.reduce((total, item) => {
        return total + Number(item.value || 0);
      }, 0);
    },

    getPaymentColor(key) {
      return PAYMENT_CHART_COLORS[key] || PAYMENT_CHART_COLORS.other;
    },

    chartOptions(prefix) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex] || {};
              const label = dataset.label ? `${dataset.label}: ` : "";
              const value =
                tooltipItem.yLabel !== undefined
                  ? tooltipItem.yLabel
                  : tooltipItem.value;
              return `${label}${this.formatChartValue(value, prefix)}`;
            }
          }
        },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                callback: value => this.formatChartValue(value, prefix)
              },
              gridLines: { color: "rgba(15, 23, 42, 0.08)" }
            }
          ],
          xAxes: [{ gridLines: { display: false } }]
        }
      };
    },

    paymentChartOptions() {
      const options = this.chartOptions("R$");
      options.layout = { padding: { top: 18 } };
      return options;
    },

    salesTopStoresChartOptions(total) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 62,
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 10,
            fontSize: 10,
            padding: 8
          }
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex] || {};
              const rawValue =
                dataset.rawValues && dataset.rawValues[tooltipItem.index]
                  ? dataset.rawValues[tooltipItem.index]
                  : Number(dataset.data[tooltipItem.index] || 0) * 100;
              const percentage = total
                ? ` (${((rawValue / total) * 100).toFixed(1)}%)`
                : "";
              return `${data.labels[tooltipItem.index]}: ${this.formatCurrency(
                rawValue
              )}${percentage}`;
            }
          }
        }
      };
    },

    paymentPercentagePlugin() {
      return {
        afterDatasetsDraw(chart) {
          const dataset = chart.data.datasets[0];
          const meta = chart.getDatasetMeta(0);
          if (!meta || !dataset || !dataset.percentages) return;

          const ctx = chart.ctx;
          ctx.save();
          ctx.fillStyle = "#475467";
          ctx.font = "600 10px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";

          meta.data.forEach((bar, index) => {
            const percentage = dataset.percentages[index];
            if (!percentage || !bar || !bar._model) return;
            ctx.fillText(percentage, bar._model.x, bar._model.y - 4);
          });

          ctx.restore();
        }
      };
    },

    getDeltaPercentage(current, previous) {
      const currentValue = Number(current || 0);
      const previousValue = Number(previous || 0);
      if (!previousValue && !currentValue) return null;
      if (!previousValue) return 100;
      return ((currentValue - previousValue) / previousValue) * 100;
    },

    formatDelta(delta) {
      if (delta === null || delta === undefined) return "sem comparação";
      const signal = delta > 0 ? "+" : "";
      return `${signal}${delta.toFixed(1)}% vs anterior`;
    },

    salesDeltaClass(delta) {
      if (delta === null || delta === undefined) return "neutral";
      return delta >= 0 ? "positive" : "negative";
    },

    renderProductCharts() {
      this.$nextTick(() => {
        this.renderTopProductsChart();
      });
    },

    renderTopProductsChart() {
      if (!this.$refs.topProductsChart) return;
      if (this.topProductsChart) this.topProductsChart.destroy();

      const metric = this.productMetricMode;
      const data = this.topProductsChartRows;
      const prefix = metric === "revenue" ? "R$" : "";

      this.topProductsChart = new Chart(this.$refs.topProductsChart, {
        type: "horizontalBar",
        data: {
          labels: data.map(item => this.shortProductChartLabel(item.name)),
          datasets: [
            {
              label: metric === "revenue" ? "Faturamento" : "Quantidade",
              data: data.map(item => {
                const value = this.getProductMetricValue(item, metric);
                return metric === "revenue" ? value / 100 : value;
              }),
              backgroundColor: data.map(item => item.abcColor),
              abcLabels: data.map(item => {
                return `${item.abcClass} ${item.cumulativePercentageLabel}`;
              }),
              fullLabels: data.map(item => item.name),
              cumulativePercentages: data.map(item => {
                return Number((item.cumulativePercentage * 100).toFixed(1));
              }),
              xAxisID: "value"
            }
          ]
        },
        options: this.topProductsChartOptions(prefix),
        plugins: [
          this.productScaleSyncPlugin(),
          this.cumulativePercentageLinePlugin(),
          this.productValueLabelPlugin(metric),
          this.productAxisLabelTitlePlugin()
        ]
      });
    },

    topProductsChartOptions(prefix) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
          labels: {
            boxWidth: 10,
            fontSize: 10
          }
        },
        layout: { padding: { top: 4, right: 56 } },
        tooltips: {
          callbacks: {
            title: (tooltipItems, data) => {
              const tooltipItem = tooltipItems[0];
              const dataset = data.datasets[tooltipItem.datasetIndex];
              return (
                (dataset.fullLabels && dataset.fullLabels[tooltipItem.index]) ||
                data.labels[tooltipItem.index]
              );
            },
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const abcLabel =
                dataset.abcLabels && dataset.abcLabels[tooltipItem.index];
              const valueLabel = prefix
                ? this.formatChartValue(tooltipItem.xLabel, prefix)
                : tooltipItem.xLabel;
              return abcLabel
                ? `${valueLabel} | Classe ${abcLabel}`
                : valueLabel;
            }
          }
        },
        scales: {
          xAxes: [
            {
              id: "value",
              type: "linear",
              display: false,
              position: "bottom",
              ticks: {
                beginAtZero: true,
                min: 0,
                callback: value =>
                  prefix ? this.formatChartValue(value, prefix) : value
              },
              gridLines: { color: "rgba(15, 23, 42, 0.08)" }
            },
            {
              id: "percentage",
              type: "linear",
              display: false,
              position: "bottom",
              ticks: {
                beginAtZero: true,
                min: 0,
                max: 100,
                callback: value => `${value}%`
              },
              gridLines: { display: false }
            }
          ],
          yAxes: [
            {
              gridLines: { display: false },
              ticks: {
                autoSkip: false,
                fontSize: 11
              },
              barPercentage: 0.7,
              categoryPercentage: 0.7
            }
          ]
        }
      };
    },

    shortProductChartLabel(value) {
      const label = String(value || "");
      return label.length > 18 ? `${label.slice(0, 18)}...` : label;
    },

    productScaleSyncPlugin() {
      return {
        afterDraw: chart => {
          const scale = chart.scales.percentage;
          const width = chart.width || (chart.canvas && chart.canvas.width);
          if (!scale || !width) return;

          const nextInsets = {
            left: Math.round(scale.left),
            right: Math.max(0, Math.round(width - scale.right))
          };
          if (
            this.topProductsScaleInsets.left === nextInsets.left &&
            this.topProductsScaleInsets.right === nextInsets.right
          ) {
            return;
          }
          this.$nextTick(() => {
            this.topProductsScaleInsets = nextInsets;
          });
        }
      };
    },

    productValueLabelPlugin(metric) {
      return {
        afterDatasetsDraw: chart => {
          const dataset = chart.data.datasets[0];
          const meta = chart.getDatasetMeta(0);
          if (!dataset || !meta) return;

          const ctx = chart.ctx;
          const chartArea = chart.chartArea || {};
          ctx.save();
          ctx.fillStyle = "#475467";
          ctx.font = "700 10px sans-serif";
          ctx.textBaseline = "middle";

          meta.data.forEach((bar, index) => {
            if (!bar || !bar._model) return;
            const rawValue = dataset.data[index];
            const label =
              metric === "revenue"
                ? this.formatCurrency(rawValue * 100)
                : this.formatNumber(rawValue);
            const x = Math.min(bar._model.x + 6, (chartArea.right || 0) + 46);
            ctx.textAlign = x > (chartArea.right || 0) ? "right" : "left";
            ctx.fillText(label, x, bar._model.y);
          });

          ctx.restore();
        }
      };
    },

    productAxisLabelTitlePlugin() {
      const hideTooltip = chart => {
        if (!chart.canvas) return;
        chart.$productAxisTooltip = null;
        chart.canvas.style.cursor = "";
        chart.draw();
      };

      return {
        afterInit(chart) {
          if (!chart.canvas || chart.$productAxisTooltipListener) return;

          const handleMove = event => {
            if (!chart.chartArea) return;

            const rect = chart.canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            const chartArea = chart.chartArea;
            const isOverAxisLabels =
              x < chartArea.left &&
              x > 0 &&
              y >= chartArea.top &&
              y <= chartArea.bottom;

            if (!isOverAxisLabels) {
              hideTooltip(chart);
              return;
            }

            const dataset = chart.data.datasets[0];
            const meta = chart.getDatasetMeta(0);
            if (!dataset || !meta || !Array.isArray(dataset.fullLabels)) {
              hideTooltip(chart);
              return;
            }

            let closestIndex = -1;
            let closestDistance = Infinity;
            meta.data.forEach((bar, index) => {
              if (!bar || !bar._model) return;
              const distance = Math.abs(y - bar._model.y);
              if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = index;
              }
            });

            if (closestIndex < 0 || closestDistance > 16) {
              hideTooltip(chart);
              return;
            }

            chart.$productAxisTooltip = {
              label: dataset.fullLabels[closestIndex],
              x: Math.max(chartArea.left + 8, x + 18),
              y: Math.max(chartArea.top + 8, y - 30)
            };
            chart.canvas.style.cursor = "default";
            chart.draw();
          };

          const handleLeave = () => hideTooltip(chart);
          chart.canvas.addEventListener("mousemove", handleMove);
          chart.canvas.addEventListener("mouseleave", handleLeave);
          chart.$productAxisTooltipListener = { handleMove, handleLeave };
        },

        destroy(chart) {
          if (!chart.canvas || !chart.$productAxisTooltipListener) return;
          chart.canvas.removeEventListener(
            "mousemove",
            chart.$productAxisTooltipListener.handleMove
          );
          chart.canvas.removeEventListener(
            "mouseleave",
            chart.$productAxisTooltipListener.handleLeave
          );
          hideTooltip(chart);
          chart.$productAxisTooltipListener = null;
        },

        afterDraw(chart) {
          const tooltip = chart.$productAxisTooltip;
          if (!tooltip || !tooltip.label) return;

          const ctx = chart.ctx;
          const chartArea = chart.chartArea || {};
          const maxWidth = 260;
          const text = tooltip.label;
          ctx.save();
          ctx.font = "600 11px sans-serif";
          const textWidth = Math.min(ctx.measureText(text).width, maxWidth);
          const boxWidth = textWidth + 16;
          const boxHeight = 26;
          const x = Math.min(
            tooltip.x,
            Math.max(chartArea.left, chartArea.right - boxWidth - 6)
          );
          const y = Math.min(
            tooltip.y,
            Math.max(chartArea.top, chartArea.bottom - boxHeight - 6)
          );

          ctx.fillStyle = "rgba(23, 32, 51, 0.95)";
          ctx.beginPath();
          ctx.moveTo(x + 4, y);
          ctx.lineTo(x + boxWidth - 4, y);
          ctx.quadraticCurveTo(x + boxWidth, y, x + boxWidth, y + 4);
          ctx.lineTo(x + boxWidth, y + boxHeight - 4);
          ctx.quadraticCurveTo(
            x + boxWidth,
            y + boxHeight,
            x + boxWidth - 4,
            y + boxHeight
          );
          ctx.lineTo(x + 4, y + boxHeight);
          ctx.quadraticCurveTo(x, y + boxHeight, x, y + boxHeight - 4);
          ctx.lineTo(x, y + 4);
          ctx.quadraticCurveTo(x, y, x + 4, y);
          ctx.fill();

          ctx.fillStyle = "#ffffff";
          ctx.textBaseline = "middle";
          ctx.textAlign = "left";
          const label =
            ctx.measureText(text).width > maxWidth
              ? `${text.slice(0, 42)}...`
              : text;
          ctx.fillText(label, x + 8, y + boxHeight / 2);
          ctx.restore();
        }
      };
    },

    cumulativePercentageLinePlugin() {
      return {
        beforeDatasetsDraw(chart) {
          const scale = chart.scales.percentage;
          const chartArea = chart.chartArea || {};
          if (!scale) return;

          const ctx = chart.ctx;
          ctx.save();
          ctx.strokeStyle = "rgba(15, 23, 42, 0.08)";
          ctx.lineWidth = 1;
          for (let tick = 0; tick <= 100; tick += 10) {
            const x = scale.getPixelForValue(tick);
            ctx.beginPath();
            ctx.moveTo(x, chartArea.top);
            ctx.lineTo(x, chartArea.bottom);
            ctx.stroke();
          }
          ctx.restore();
        },

        afterDatasetsDraw(chart) {
          const dataset = chart.data.datasets[0];
          const meta = chart.getDatasetMeta(0);
          const scale = chart.scales.percentage;
          if (
            !dataset ||
            !meta ||
            !scale ||
            !Array.isArray(dataset.cumulativePercentages)
          ) {
            return;
          }

          const points = meta.data
            .map((bar, index) => {
              if (!bar || !bar._model) return null;
              return {
                x: scale.getPixelForValue(dataset.cumulativePercentages[index]),
                y: bar._model.y
              };
            })
            .filter(Boolean);

          if (!points.length) return;

          const ctx = chart.ctx;
          ctx.save();
          ctx.strokeStyle = "#172033";
          ctx.fillStyle = "#172033";
          ctx.lineWidth = 2;
          ctx.beginPath();
          points.forEach((point, index) => {
            if (index === 0) ctx.moveTo(point.x, point.y);
            else ctx.lineTo(point.x, point.y);
          });
          ctx.stroke();

          points.forEach(point => {
            ctx.beginPath();
            ctx.arc(point.x, point.y, 3, 0, Math.PI * 2);
            ctx.fill();
          });
          ctx.restore();
        }
      };
    },

    setProductMetricMode(mode) {
      this.productMetricMode = mode;
      this.$nextTick(this.renderTopProductsChart);
    },

    setProductDimensionMode(mode) {
      this.productDimensionMode = mode;
      this.$nextTick(this.renderTopProductsChart);
    },

    getGroupedProductRows(dimension) {
      const groups = {};

      (this.productRows || []).forEach(row => {
        const key = this.getProductDimensionKey(row, dimension);
        if (!groups[key]) {
          groups[key] = {
            id: key,
            name: this.getProductDimensionName(row, dimension),
            productCount: 0,
            grossSales: 0,
            skus: {}
          };
        }

        groups[key].productCount += Number(row.productCount || 0);
        groups[key].grossSales += Number(row.grossSales || 0);
        groups[key].skus[row.productId || row.productName] = true;
      });

      return Object.values(groups).map(group => {
        group.skusSold = Object.keys(group.skus).length;
        return group;
      });
    },

    getProductDimensionKey(row, dimension) {
      if (dimension === "category") return row.category || "uncategorized";
      if (dimension === "subcategory") {
        return row.subcategory || "uncategorized";
      }
      return row.productId || row.productName || "unknown";
    },

    getProductDimensionName(row, dimension) {
      if (dimension === "category") {
        return this.categoryNameById[row.category] || "Sem categoria";
      }
      if (dimension === "subcategory") {
        return this.subcategoryNameById[row.subcategory] || "Sem subcategoria";
      }
      return row.productName || "Produto sem nome";
    },

    getProductMetricValue(item, metric) {
      return metric === "revenue"
        ? Number(item.grossSales || 0)
        : Number(item.productCount || 0);
    },

    getVisibleProductAbcRows(rows) {
      if (!Array.isArray(rows) || rows.length <= MAX_PRODUCT_ABC_BARS) {
        return rows || [];
      }

      const visibleRowsLimit = MAX_PRODUCT_ABC_BARS - 1;
      const visibleRows = rows.slice(0, visibleRowsLimit);
      const hiddenRows = rows.slice(visibleRowsLimit);
      const lastHiddenRow = hiddenRows[hiddenRows.length - 1] || {};
      const othersRow = hiddenRows.reduce(
        (total, row) => {
          total.productCount += Number(row.productCount || 0);
          total.grossSales += Number(row.grossSales || 0);
          total.skusSold += Number(row.skusSold || 1);
          return total;
        },
        {
          id: "__others__",
          name: "Outros",
          productCount: 0,
          grossSales: 0,
          skusSold: 0
        }
      );

      return visibleRows.concat([
        Object.assign(othersRow, {
          abcClass: lastHiddenRow.abcClass || "C",
          abcColor: this.getAbcColor(lastHiddenRow.abcClass || "C"),
          cumulativePercentage: lastHiddenRow.cumulativePercentage || 1,
          cumulativePercentageLabel:
            lastHiddenRow.cumulativePercentageLabel || "100.0%"
        })
      ]);
    },

    getProductsAbcRows(rows, metric) {
      const total = rows.reduce(
        (sum, row) => sum + this.getProductMetricValue(row, metric),
        0
      );
      let accumulated = 0;

      return rows.map(row => {
        const value = this.getProductMetricValue(row, metric);
        accumulated += value;
        const percentage = total ? accumulated / total : 0;
        const abcClass =
          percentage <= 0.8 ? "A" : percentage <= 0.95 ? "B" : "C";
        return Object.assign({}, row, {
          abcClass,
          abcColor: this.getAbcColor(abcClass),
          individualPercentage: total ? value / total : 0,
          individualPercentageLabel: `${(total
            ? (value / total) * 100
            : 0
          ).toFixed(1)}%`,
          cumulativePercentage: percentage,
          cumulativePercentageLabel: `${(percentage * 100).toFixed(1)}%`
        });
      });
    },

    getAbcColor(abcClass) {
      if (abcClass === "A") return "#11a36a";
      if (abcClass === "B") return "#ffb020";
      return "#f0512d";
    },

    getAbcMatrixPriority(key) {
      if (key === "AA") return "critical";
      if (["AB", "BA", "AC", "CA"].includes(key)) return "strategic";
      if (key === "CC") return "low";
      return "middle";
    },

    getAbcMatrixDescription(key) {
      const descriptions = {
        AA:
          "Alto giro e alto faturamento. São os itens mais importantes: evite ruptura e acompanhe exposição e preço.",
        AB:
          "Alto giro e faturamento médio. Bons candidatos para melhorar margem ou exposição.",
        AC:
          "Alto giro e baixo faturamento. Tendem a ser itens de tráfego, conveniência ou preço baixo; não podem faltar.",
        BA:
          "Giro médio e alto faturamento. Acompanhe disponibilidade e ações para aumentar recorrência.",
        BB:
          "Giro médio e faturamento médio. Desempenho equilibrado para monitorar como referência de mix e reposição.",
        BC:
          "Giro médio e baixo faturamento. Revise margem, preço e papel do item dentro do sortimento.",
        CA:
          "Baixo giro e alto faturamento. Itens premium ou caros; controle disponibilidade sem excesso de estoque.",
        CB:
          "Baixo giro e faturamento médio. Avalie se precisa de melhor exposição, promoção ou substituição.",
        CC:
          "Baixo giro e baixo faturamento. Revise espaço, exposição ou necessidade no mix."
      };
      return descriptions[key] || "";
    },

    renderMap() {
      if (!this.$refs.storesMap) return;

      if (this.map && this.map.getContainer() !== this.$refs.storesMap) {
        this.destroyMap();
      }

      if (!this.map) {
        this.map = L.map(this.$refs.storesMap, {
          zoomControl: false,
          attributionControl: false
        });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19
        }).addTo(this.map);
        L.control.zoom({ position: "bottomright" }).addTo(this.map);
        this.storeMarkersLayer = L.layerGroup().addTo(this.map);
        this.pulseLayer = L.layerGroup().addTo(this.map);
        this.map.on("moveend zoomend", this.saveLeafletView);
      }

      this.renderStoreMarkers();
      if (this.restoreLeafletViewAfterLoad && this.savedLeafletView) {
        this.restoreLeafletView();
        this.restoreLeafletViewAfterLoad = false;
      } else {
        this.centerLeafletOnStores();
      }

      window.setTimeout(() => {
        if (this.map) this.map.invalidateSize();
      }, 100);
    },

    renderStoreMarkers() {
      if (!this.storeMarkersLayer) return;

      this.storeMarkersLayer.clearLayers();
      if (!this.showMapPins) return;

      this.storesWithCoordinates.forEach(store => {
        const coordinates = this.parseCoordinates(store.coordinates);
        if (!coordinates) return;

        L.marker([coordinates.lat, coordinates.lng], {
          icon: this.getStoreMarkerIcon()
        })
          .bindTooltip(store.storeName, { direction: "top" })
          .addTo(this.storeMarkersLayer);
      });
    },

    centerLeafletOnStores() {
      if (!this.map) return;

      const bounds = [];
      this.storesWithCoordinates.forEach(store => {
        const coordinates = this.parseCoordinates(store.coordinates);
        if (!coordinates) return;
        bounds.push([coordinates.lat, coordinates.lng]);
      });

      if (bounds.length === 1) {
        this.map.setView(bounds[0], 14);
      } else if (bounds.length > 1) {
        this.map.fitBounds(bounds, { padding: [28, 28], maxZoom: 14 });
      } else {
        this.map.setView([-23.5505, -46.6333], 11);
      }

      window.setTimeout(() => {
        if (this.map) this.map.invalidateSize();
      }, 100);
    },

    toggleMapPins() {
      this.showMapPins = !this.showMapPins;
      this.renderStoreMarkers();
    },

    saveLeafletView() {
      if (!this.map) return;
      const center = this.map.getCenter();
      this.savedLeafletView = {
        lat: center.lat,
        lng: center.lng,
        zoom: this.map.getZoom()
      };
      this.scheduleSaveDashboardBISettings();
    },

    restoreLeafletView() {
      if (!this.map || !this.savedLeafletView) return;
      this.map.setView(
        [this.savedLeafletView.lat, this.savedLeafletView.lng],
        this.savedLeafletView.zoom
      );

      window.setTimeout(() => {
        if (this.map) this.map.invalidateSize();
      }, 100);
    },

    getStoreMarkerIcon() {
      return L.divIcon({
        className: "store-map-marker",
        html: "<span></span>",
        iconSize: [18, 18],
        iconAnchor: [9, 9]
      });
    },

    getSalePulseIcon() {
      return L.divIcon({
        className: "sale-map-pulse",
        html: "<span></span>",
        iconSize: [28, 28],
        iconAnchor: [14, 14]
      });
    },

    connectSocket() {
      if (!this.sessionToken) return;
      this.socketStatus = "connecting";
      this.socket = io(this.getSocketEndpoint(), {
        transports: ["websocket"],
        auth: { token: this.sessionToken }
      });

      this.socket.on("connect", () => {
        this.socketStatus = "connected";
      });
      this.socket.on("disconnect", () => {
        this.socketStatus = "disconnected";
      });
      this.socket.on("connect_error", () => {
        this.socketStatus = "disconnected";
      });
      this.socket.on("MERCHANT_NEW_TRANSACTION", this.handleNewTransaction);
    },

    getSocketEndpoint() {
      const rawEndpoint =
        process.env.VUE_APP_SOCKET_SERVER_ENDPOINT ||
        process.env.VUE_APP_API_ENDPOINT ||
        window.location.origin;

      try {
        const url = new URL(rawEndpoint, window.location.origin);
        return `${url.protocol}//${url.host}/merchant/v1/ws`;
      } catch (err) {
        return "/merchant/v1/ws";
      }
    },

    handleNewTransaction(payload) {
      if (!this.shouldApplyRealtimeTransaction(payload)) return;

      const transaction = this.normalizeTransaction(payload);
      this.recentTransactions.unshift(transaction);
      this.recentTransactions = this.recentTransactions.slice(
        0,
        MAX_RECENT_TRANSACTIONS
      );

      this.summary.transactionsCount += 1;
      this.summary.grossSales += transaction.grossSales;
      this.summary.netSales += transaction.netSales;
      this.summary.discounts += transaction.discounts;
      this.addPaymentValues(payload.payments, transaction);
      this.addHourlyValue(transaction.createdAt, transaction.grossSales);
      this.addStoreValue(transaction);
      this.addProductValues(payload.products);
      this.addSalePulse(transaction);
      this.renderCharts();
    },

    shouldApplyRealtimeTransaction(payload) {
      if (!this.selectedStoreIds.length) return true;
      const storeId =
        payload.store &&
        (payload.store._id || payload.store.id || payload.store.storeId);

      return this.selectedStoreIds.includes(String(storeId));
    },

    normalizeTransaction(payload) {
      const createdAt = payload.createdAt
        ? this.moment(payload.createdAt)
        : this.moment();
      return {
        id: payload.transactionId || payload._id || `${Date.now()}`,
        value: Number(
          payload.value || payload.netSales || payload.grossSales || 0
        ),
        grossSales: Number(
          payload.grossSales || payload.value || payload.netSales || 0
        ),
        netSales: Number(
          payload.netSales || payload.value || payload.grossSales || 0
        ),
        discounts: Number(
          payload.discounts && typeof payload.discounts === "object"
            ? payload.discounts.total || 0
            : payload.discounts || 0
        ),
        paymentMethod: payload.paymentMethod || "appCredit",
        storeId:
          (payload.store && (payload.store._id || payload.store.id)) ||
          payload.storeId,
        storeName:
          (payload.store && payload.store.name) ||
          payload.storeName ||
          "Loja sem nome",
        createdAt,
        createdAtLabel: createdAt.format("HH:mm:ss"),
        coordinates: payload.store && payload.store.coordinates
      };
    },

    addPaymentValues(payments, transaction) {
      const summaryPayments = Object.assign({}, this.summary.payments);
      if (payments && typeof payments === "object") {
        Object.keys(payments).forEach(key => {
          summaryPayments[key] =
            Number(summaryPayments[key] || 0) + Number(payments[key] || 0);
        });
      } else {
        const key = transaction.paymentMethod || "appCredit";
        summaryPayments[key] =
          Number(summaryPayments[key] || 0) + transaction.value;
      }
      this.summary.payments = summaryPayments;
    },

    addHourlyValue(createdAt, value) {
      const hour = Number(createdAt.format("H"));
      const salesByHour = (this.summary.salesByHour || []).slice();
      while (salesByHour.length < 24) salesByHour.push(0);
      salesByHour[hour] = Number(salesByHour[hour] || 0) + value;
      this.summary.salesByHour = salesByHour;
    },

    addStoreValue(transaction) {
      const storeKey = transaction.storeId || transaction.storeName;
      const topStores = this.topStores.slice();
      const storeIndex = topStores.findIndex(store => {
        return (store.storeId || store.storeName) === storeKey;
      });

      if (
        transaction.storeId &&
        this.activeStoreIds.indexOf(String(transaction.storeId)) === -1
      ) {
        this.activeStoreIds = this.activeStoreIds.concat([
          String(transaction.storeId)
        ]);
        this.$set(this.summary, "activeStores", this.activeStoreIds.length);
      }

      if (storeIndex >= 0) {
        const store = Object.assign({}, topStores[storeIndex]);
        store.grossSales =
          Number(store.grossSales || 0) + transaction.grossSales;
        store.transactionsCount = Number(store.transactionsCount || 0) + 1;
        this.$set(topStores, storeIndex, store);
      } else {
        topStores.push({
          storeId: transaction.storeId,
          storeName: transaction.storeName,
          grossSales: transaction.grossSales,
          transactionsCount: 1
        });
      }

      this.topStores = topStores
        .sort((a, b) => Number(b.grossSales || 0) - Number(a.grossSales || 0))
        .slice(0, 10);
    },

    addProductValues(products) {
      if (!Array.isArray(products) || products.length === 0) return;

      const topProducts = this.topProducts.slice();
      const topCategories = this.topCategories.slice();
      const topSubcategories = this.topSubcategories.slice();
      products.forEach(product => {
        const grossSales = Number(product.value || product.grossSales || 0);
        const productCount = Number(
          product.quantity || product.productCount || 1
        );
        const category = product.category || product.c;
        const subcategory = product.subcategory || product.s;
        const productKey = product.productId || product.productName;
        const productIndex = topProducts.findIndex(item => {
          return (item.productId || item.productName) === productKey;
        });

        if (productIndex >= 0) {
          const currentProduct = Object.assign({}, topProducts[productIndex]);
          currentProduct.grossSales =
            Number(currentProduct.grossSales || 0) + grossSales;
          currentProduct.productCount =
            Number(currentProduct.productCount || 0) + productCount;
          currentProduct.transactionsCount =
            Number(currentProduct.transactionsCount || 0) + 1;
          this.$set(topProducts, productIndex, currentProduct);
        } else {
          topProducts.push({
            productId: product.productId,
            productName: product.productName || "Produto sem nome",
            barcode: product.barcode,
            brand: product.brand,
            imageURL: product.imageURL,
            category,
            subcategory,
            grossSales,
            productCount,
            transactionsCount: 1
          });
        }

        this.addRankingAggregate(
          topCategories,
          "category",
          category,
          grossSales,
          productCount
        );
        this.addRankingAggregate(
          topSubcategories,
          "subcategory",
          subcategory,
          grossSales,
          productCount
        );
      });

      this.topProducts = topProducts
        .sort((a, b) => Number(b.grossSales || 0) - Number(a.grossSales || 0))
        .slice(0, 10);
      this.topCategories = topCategories
        .sort((a, b) => Number(b.grossSales || 0) - Number(a.grossSales || 0))
        .slice(0, 10);
      this.topSubcategories = topSubcategories
        .sort((a, b) => Number(b.grossSales || 0) - Number(a.grossSales || 0))
        .slice(0, 10);
    },

    addRankingAggregate(rows, keyName, key, grossSales, productCount) {
      if (!key) return;

      const normalizedKey = String(key);
      const index = rows.findIndex(row => {
        return String(row[keyName] || "") === normalizedKey;
      });

      if (index >= 0) {
        const current = Object.assign({}, rows[index]);
        current.grossSales = Number(current.grossSales || 0) + grossSales;
        current.productCount = Number(current.productCount || 0) + productCount;
        current.transactionsCount = Number(current.transactionsCount || 0) + 1;
        this.$set(rows, index, current);
        return;
      }

      rows.push({
        [keyName]: normalizedKey,
        grossSales,
        productCount,
        transactionsCount: 1
      });
    },

    addSalePulse(transaction) {
      if (!this.map || !this.pulseLayer) return;
      const coordinates = this.parseCoordinates(transaction.coordinates);
      if (!coordinates) return;

      const marker = L.marker([coordinates.lat, coordinates.lng], {
        icon: this.getSalePulseIcon()
      }).addTo(this.pulseLayer);

      window.setTimeout(() => {
        if (this.pulseLayer) this.pulseLayer.removeLayer(marker);
      }, 5000);
    },

    parseCoordinates(coordinates) {
      if (Array.isArray(coordinates) && coordinates.length >= 2) {
        const lng = Number(coordinates[0]);
        const lat = Number(coordinates[1]);
        if (!Number.isNaN(lng) && !Number.isNaN(lat)) return { lng, lat };
      }

      if (coordinates && typeof coordinates === "object") {
        const lng = Number(
          coordinates.lng !== undefined
            ? coordinates.lng
            : coordinates.longitude
        );
        const lat = Number(
          coordinates.lat !== undefined ? coordinates.lat : coordinates.latitude
        );
        if (!Number.isNaN(lng) && !Number.isNaN(lat)) return { lng, lat };
      }

      return null;
    },

    formatCurrency(value) {
      return (Number(value || 0) / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL"
      });
    },

    formatChartValue(value, prefix) {
      const numericValue = Number(value || 0);
      if (prefix === "R$") {
        return numericValue.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }

      if (prefix) {
        return `${prefix} ${numericValue.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`;
      }

      return numericValue.toLocaleString("pt-BR");
    },

    formatNumber(value) {
      return Number(value || 0).toLocaleString("pt-BR");
    },

    paymentLabel(key) {
      const labels = {
        tefCredit: "TEF crédito",
        tefDebit: "TEF débito",
        pix: "Pix",
        pixCelcoin: "Pix Celcoin",
        pixPagbank: "Pix PagBank",
        pixAdyen: "Pix Adyen",
        prepaid: "Pré-pago",
        appCredit: "Crédito app",
        terminalCredit: "POS crédito",
        terminalDebit: "POS débito",
        applePay: "Apple Pay",
        applePayCredit: "Apple Pay crédito",
        applePayDebit: "Apple Pay débito",
        googlePay: "Google Pay",
        googlePayCredit: "Google Pay crédito",
        googlePayDebit: "Google Pay débito",
        voucher: "Voucher",
        voucherSodexo: "Sodexo",
        voucherAlelo: "Alelo",
        voucherTicket: "Ticket",
        voucherVr: "VR",
        voucherVerocard: "Verocard",
        voucherBen: "Ben",
        buyNowPayLater: "Compras a faturar",
        discounts: "Descontos",
        tef: "TEF"
      };

      return labels[key] || key;
    },

    getStoreRankStyle(index) {
      const colors = [
        { background: "#dcf7ea", color: "#087443" },
        { background: "#eaf8df", color: "#3f7d20" },
        { background: "#f7f7d4", color: "#6f6b00" },
        { background: "#fff4d6", color: "#8a5a00" },
        { background: "#ffe9c2", color: "#9a4d00" },
        { background: "#fff4f2", color: "#d24428" },
        { background: "#ffefea", color: "#b53723" },
        { background: "#f2f4f7", color: "#475467" },
        { background: "#eaecf0", color: "#344054" },
        { background: "#e4e7ec", color: "#1d2939" }
      ];

      return colors[Math.min(index, colors.length - 1)];
    }
  },

  watch: {
    dashboardStores(stores) {
      if (!stores.length || !this.selectedStoreIds.length) return;

      const availableStoreIds = stores.map(store => String(store.storeId));
      const selectedStoreIds = this.selectedStoreIds.filter(storeId => {
        return availableStoreIds.includes(String(storeId));
      });

      if (selectedStoreIds.length !== this.selectedStoreIds.length) {
        this.selectedStoreIds = selectedStoreIds;
        this.dashboardScopeSearch = this.dashboardScopeLabel;
      }
    },

    activeTab(newTab, oldTab) {
      this.scheduleSaveDashboardBISettings();
      if (oldTab === "realtime-transactions") {
        this.destroyRealtimeCharts();
        this.destroyMap();
      }
      if (oldTab === "sales") this.destroySalesCharts();
      if (oldTab === "products") this.destroyProductCharts();
      if (this.isApplyingDashboardBISettings) return;
      this.getActiveDashboardData();
    },

    sidebarCollapsed() {
      this.scheduleSaveDashboardBISettings();
    },

    selectedStoreIds: {
      handler() {
        this.scheduleSaveDashboardBISettings();
      },
      deep: true
    },

    showMapPins() {
      this.scheduleSaveDashboardBISettings();
    },

    comparisonDate() {
      this.scheduleSaveDashboardBISettings();
    },

    topRankingMode() {
      this.scheduleSaveDashboardBISettings();
    },

    paymentChartMode() {
      this.scheduleSaveDashboardBISettings();
    },

    productsStartDate() {
      this.ensureValidSalesDailyGroupMode();
      this.scheduleSaveDashboardBISettings();
    },

    productsEndDate() {
      this.ensureValidSalesDailyGroupMode();
      this.scheduleSaveDashboardBISettings();
    },

    productMetricMode() {
      this.scheduleSaveDashboardBISettings();
    },

    productDimensionMode() {
      this.scheduleSaveDashboardBISettings();
    },

    matrixDimensionMode() {
      this.scheduleSaveDashboardBISettings();
    },

    salesComparisonEnabled() {
      this.scheduleSaveDashboardBISettings();
      this.renderSalesDailyChart();
    },

    salesComparisonStartDate() {
      this.scheduleSaveDashboardBISettings();
    },

    salesDailyGroupMode() {
      this.scheduleSaveDashboardBISettings();
      this.renderSalesDailyChart();
    },

    salesTimeMode() {
      this.scheduleSaveDashboardBISettings();
      this.renderSalesHourlyChart();
    }
  },

  mounted() {
    this.applyDashboardBISettings();
    this.$store.dispatch("getCategories").finally(this.getActiveDashboardData);
    this.connectSocket();
  },

  beforeDestroy() {
    if (this.socket) this.socket.disconnect();
    this.destroyRealtimeCharts();
    this.destroySalesCharts();
    this.destroyProductCharts();
    this.detachSalesComparisonPreviewHandlers();
    this.clearSalesComparisonPreviewClasses();
    this.saveDashboardBISettingsNow();
    this.destroyMap();
  }
};
</script>

<style scoped>
.merchant-realtime-dashboard {
  height: calc(100vh - 3.25rem);
  display: flex;
  background: var(--dashboard-navbar);
  color: #172033;
  margin-top: 3.25rem;
  overflow: hidden;
}

.dashboard-sidebar {
  width: 220px;
  min-height: calc(100vh - 3.25rem);
  padding: 12px 10px;
  background: var(--dashboard-navbar);
  color: #fff;
  transition: width 0.2s ease;
}

.dashboard-sidebar.collapsed {
  width: 68px;
}

.sidebar-header {
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.dashboard-sidebar.collapsed .sidebar-header,
.dashboard-sidebar.collapsed .sidebar-item {
  justify-content: center;
}

.sidebar-toggle,
.sidebar-item {
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #d8deea;
  cursor: pointer;
}

.sidebar-toggle {
  width: 34px;
  height: 34px;
  background: rgba(255, 255, 255, 0.08);
}

.sidebar-item {
  width: 100%;
  min-height: 42px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  font-size: 14px;
}

.sidebar-item:hover,
.sidebar-item.active {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  box-shadow: inset 3px 0 0 var(--dashboard-primary);
}

.sidebar-brand {
  font-size: 18px;
  font-weight: 700;
  padding: 0 8px;
}

.dashboard-content {
  flex: 1;
  min-width: 0;
  padding: 16px;
  overflow: hidden;
  background: #f3f5f8;
  border-top-left-radius: 16px;
}

.dashboard-header,
.panel-title,
.transaction-meta,
.store-row,
.store-info {
  display: flex;
  align-items: center;
}

.dashboard-header,
.panel-title,
.transaction-meta,
.store-row {
  justify-content: space-between;
}

.panel-title {
  margin-bottom: 8px;
}

.dashboard-header {
  margin-bottom: 12px;
  position: relative;
  z-index: 50;
}

.dashboard-header h1 {
  font-size: 24px;
  margin: 0;
}

.dashboard-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.dashboard-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.dashboard-export-button {
  height: 30px;
  border: 1px solid #dde3ee;
  border-radius: 6px;
  background: #ffffff;
  color: #475467;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  padding: 0 10px;
}

.dashboard-export-button:hover {
  border-color: var(--dashboard-primary);
  color: var(--dashboard-primary-text);
}

.dashboard-export-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.sales-comparison-toggle {
  border: 1px solid #dde3ee;
  border-radius: 6px;
  background: #ffffff;
  color: #475467;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  height: 30px;
  padding: 0 10px;
}

.sales-comparison-toggle.active {
  border-color: var(--dashboard-primary);
  background: var(--dashboard-primary-soft);
  color: var(--dashboard-primary-text);
}

.sales-comparison-period {
  color: #667085;
  font-size: 12px;
  white-space: nowrap;
}

.sales-comparison-period.disabled {
  opacity: 0.55;
}

.dashboard-scope-combobox {
  position: relative;
  width: 260px;
  max-width: 32vw;
}

.dashboard-scope-input {
  width: 100%;
  height: 34px;
  border: 1px solid #dde3ee;
  border-radius: 6px;
  background: #ffffff;
  color: #172033;
  font-size: 13px;
  padding: 0 10px;
}

.dashboard-scope-input:focus {
  border-color: var(--dashboard-primary);
  box-shadow: 0 0 0 2px var(--dashboard-primary-focus);
  outline: none;
}

.dashboard-scope-dropdown {
  position: absolute;
  top: 38px;
  left: 0;
  z-index: 1000;
  width: 340px;
  max-width: 46vw;
  max-height: 320px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #dde3ee;
  border-radius: 8px;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.16);
  padding: 6px;
}

.dashboard-scope-dropdown button {
  width: 100%;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #172033;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  padding: 8px 10px;
  text-align: left;
}

.dashboard-scope-check {
  width: 14px;
  flex: 0 0 14px;
  color: var(--dashboard-primary);
  font-size: 10px;
}

.dashboard-scope-dropdown button:hover,
.dashboard-scope-dropdown button.highlighted {
  background: var(--dashboard-primary-soft);
  color: var(--dashboard-primary-text);
}

.dashboard-scope-dropdown button.active {
  background: var(--dashboard-primary-muted);
  color: var(--dashboard-primary-text);
  font-weight: 700;
}

.dashboard-scope-dropdown button.active:hover,
.dashboard-scope-dropdown button.active.highlighted {
  background: var(--dashboard-primary-muted);
  color: var(--dashboard-primary-text);
}

.connection-status {
  border-radius: 6px;
  padding: 6px 10px;
  background: #ffefea;
  color: #d24428;
  font-size: 12px;
}

.connection-status.connected {
  background: #dcf7ea;
  color: #087443;
}

.connection-status.connecting {
  background: #ffefea;
  color: #d24428;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 390px;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 12px;
  height: calc(100vh - 3.25rem - 32px - 41px);
  overflow: visible;
}

.summary-strip {
  display: grid;
  grid-template-columns: minmax(150px, 1.28fr) repeat(4, minmax(0, 0.93fr));
  gap: 10px;
}

.metric,
.chart-panel,
.transactions-panel,
.map-panel {
  background: #fff;
  border: 1px solid #dde3ee;
  border-radius: 8px;
}

.metric {
  padding: 10px 12px;
  border-top: 3px solid var(--dashboard-primary);
}

.metric span,
.transaction-row span,
.panel-title span {
  color: #667085;
  font-size: 12px;
}

.metric strong {
  display: block;
  margin-top: 4px;
  font-size: 20px;
}

.metric small {
  display: block;
  color: #667085;
  font-size: 11px;
  line-height: 1;
  margin-top: 2px;
}

.metric-chargeback {
  border-top-color: #08080a;
}

.map-panel {
  grid-column: 2;
  grid-row: 1 / span 2;
  padding: 12px;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.leaflet-map-wrapper {
  position: relative;
}

.stores-map {
  height: 330px;
  border-radius: 8px;
  overflow: hidden;
  background: #e8f1f8;
}

.map-title-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.map-control-button {
  border: 1px solid #dde3ee;
  border-radius: 6px;
  background: #ffffff;
  color: #667085;
  cursor: pointer;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 5px 7px;
  text-transform: uppercase;
}

.map-control-button.active {
  border-color: var(--dashboard-primary);
  background: var(--dashboard-primary-soft);
  color: var(--dashboard-primary-text);
}

.map-center-button {
  position: absolute;
  left: 10px;
  bottom: 10px;
  z-index: 500;
  width: 28px;
  height: 28px;
  border: 1px solid #b8c2cc;
  border-radius: 4px;
  background: #ffffff;
  color: #172033;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.22);
}

.map-center-button:hover {
  background: #f8fafc;
}

.charts-column {
  grid-column: 1;
  grid-row: 2;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(150px, 1.28fr) repeat(4, minmax(0, 0.93fr));
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  overflow: visible;
}

.chart-panel,
.transactions-panel {
  padding: 12px;
}

.chart-panel canvas {
  height: calc(100% - 30px) !important;
}

.transactions-panel {
  grid-column: 1;
  grid-row: 1 / span 2;
  min-height: 0;
  overflow: hidden;
}

.hourly-chart-panel,
.payment-chart-panel {
  grid-column: 2 / span 4;
}

.hourly-chart-panel {
  grid-row: 1;
  position: relative;
  z-index: 1;
  overflow: visible;
}

.payment-chart-panel {
  grid-row: 2;
}

.payment-chart-panel canvas {
  height: calc(100% - 42px) !important;
}

.products-dashboard {
  height: calc(100vh - 3.25rem - 32px - 41px);
  overflow: hidden;
}

.sales-dashboard {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 3.25rem - 32px - 41px);
  overflow: hidden;
}

.period-filter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.period-filter span {
  color: #667085;
  font-size: 12px;
}

.period-date-picker {
  width: 132px;
}

.sales-comparison-date-picker {
  width: 164px;
}

>>> .sales-comparison-date-picker .input[disabled] {
  background: #f2f4f7;
  border-color: #e4e7ec;
  color: #98a2b3;
  cursor: not-allowed;
}

>>> .sales-comparison-date-picker.disabled .icon {
  color: #98a2b3;
}

>>> .sales-comparison-date-picker .datepicker-cell.sales-comparison-preview {
  background: var(--dashboard-primary-soft);
  color: var(--dashboard-primary-text);
}

>>> .sales-comparison-date-picker .datepicker-cell.is-today {
  border: 1px solid transparent !important;
  box-shadow: none !important;
}

>>> .sales-comparison-date-picker
  .datepicker-cell.sales-comparison-preview
  span {
  color: inherit;
}

>>> .sales-comparison-date-picker
  .datepicker-cell.sales-comparison-preview-start,
>>> .sales-comparison-date-picker
  .datepicker-cell.sales-comparison-preview-end {
  background: var(--dashboard-primary-muted);
  color: var(--dashboard-primary-text);
  font-weight: 700;
}

>>> .period-date-picker .input {
  height: 30px;
  border: 1px solid #dde3ee;
  border-radius: 6px;
  background: #ffffff;
  color: #172033;
  font-size: 12px;
  padding: 0 7px 0 30px;
}

>>> .period-date-picker .input:focus {
  border-color: var(--dashboard-primary);
  box-shadow: 0 0 0 2px var(--dashboard-primary-focus);
  outline: none;
}

>>> .period-date-picker .icon {
  height: 30px;
  width: 28px;
}

>>> .period-date-picker .dropdown-menu {
  z-index: 2000;
}

.products-summary-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 220px));
  gap: 10px;
  margin-bottom: 12px;
}

.sales-summary-strip {
  display: grid;
  grid-template-columns: repeat(5, minmax(150px, 220px));
  gap: 10px;
  margin-bottom: 12px;
}

.sales-summary-strip.comparing .metric {
  padding-bottom: 7px;
  padding-top: 8px;
}

.sales-summary-strip.comparing .metric strong {
  font-size: 18px;
  margin-top: 2px;
}

.sales-summary-strip .metric small.positive {
  color: #087443;
}

.sales-summary-strip .metric small.negative {
  color: #d24428;
}

.sales-summary-strip .metric small.neutral {
  color: #667085;
}

.sales-charts-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(280px, 0.65fr);
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
  flex: 1 1 auto;
  gap: 12px;
  min-height: 0;
}

.sales-daily-chart-panel {
  grid-column: 1;
  grid-row: 1;
}

.sales-payment-chart-panel {
  grid-column: 1;
  grid-row: 2;
}

.sales-hourly-chart-panel {
  grid-column: 2;
  grid-row: 1;
}

.sales-daily-panel-title,
.sales-time-panel-title {
  gap: 8px;
}

.sales-top-stores-panel {
  grid-column: 2;
  grid-row: 2;
  overflow: hidden;
}

.sales-top-stores-panel canvas {
  height: calc(100% - 30px) !important;
}

.products-charts-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  height: calc(100% - 96px);
  min-height: 0;
}

.products-chart-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.products-chart-panel canvas {
  height: calc(100% - 58px) !important;
}

.top-products-sticky-scale {
  align-items: flex-end;
  background: #ffffff;
  height: 32px;
  padding-bottom: 6px;
  position: relative;
  z-index: 2;
}

.top-products-percentage-scale {
  height: 24px;
  position: relative;
}

.top-products-percentage-scale span {
  color: #667085;
  font-size: 11px;
  position: absolute;
  top: 11px;
  transform: translateX(-50%);
  white-space: nowrap;
}

.top-products-percentage-scale strong {
  color: #667085;
  font-size: 11px;
  font-weight: 500;
  left: 50%;
  position: absolute;
  top: -7px;
  transform: translateX(-50%);
  white-space: nowrap;
}

.top-products-chart-scroll {
  flex: 1 1 auto;
  height: auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  position: relative;
}

.top-products-chart-canvas {
  min-height: 220px;
  position: relative;
}

.top-products-chart-canvas canvas {
  height: 100% !important;
}

.abc-summary {
  align-items: center;
  border-top: 1px solid #edf1f7;
  display: flex;
  flex: 0 0 auto;
  gap: 10px;
  height: 42px;
  margin-top: auto;
  padding-top: 8px;
}

.abc-summary-item {
  align-items: center;
  background: #f8fafc;
  border-radius: 6px;
  color: #475467;
  display: inline-flex;
  gap: 6px;
  padding: 6px 8px;
}

.abc-summary-item span {
  border-radius: 50%;
  display: inline-block;
  height: 8px;
  width: 8px;
}

.abc-summary-item strong {
  color: #172033;
  font-size: 12px;
}

.abc-summary-item small {
  font-size: 11px;
}

.products-matrix-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.matrix-title-line {
  align-items: center;
  display: flex;
  gap: 8px;
}

.abc-matrix-help-tooltip-wrap {
  color: #667085;
  display: inline-flex;
  position: relative;
}

.abc-matrix-help-icon {
  cursor: help;
  font-size: 13px;
}

.abc-matrix-help-tooltip {
  background: rgba(23, 32, 51, 0.95);
  border-radius: 5px;
  color: #ffffff !important;
  display: none;
  font-size: 11px;
  font-weight: 500;
  left: 0;
  line-height: 1.35;
  max-width: 320px;
  min-width: 280px;
  padding: 8px 10px;
  position: absolute;
  top: calc(100% + 6px);
  white-space: normal;
  z-index: 45;
}

.abc-matrix-help-tooltip-wrap:hover .abc-matrix-help-tooltip {
  display: block;
}

.abc-reliability-tag {
  border-radius: 999px;
  color: #475467;
  display: inline-flex;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 5px 8px;
  white-space: nowrap;
}

.abc-reliability-tooltip-wrap {
  display: inline-flex;
  position: relative;
}

.abc-reliability-tooltip {
  background: rgba(23, 32, 51, 0.95);
  border-radius: 5px;
  color: #ffffff !important;
  display: none;
  font-size: 11px;
  font-weight: 500;
  left: 0;
  line-height: 1.35;
  max-width: 320px;
  min-width: 280px;
  padding: 8px 10px;
  position: absolute;
  top: calc(100% + 6px);
  white-space: normal;
  z-index: 40;
}

.abc-reliability-tooltip-wrap:hover .abc-reliability-tooltip {
  display: block;
}

.abc-reliability-tag.none {
  background: #f2f4f7;
}

.abc-reliability-tag.low {
  background: #fff4d6;
  color: #8a5a00;
}

.abc-reliability-tag.medium {
  background: #ecfdf3;
  color: #087443;
}

.abc-reliability-tag.high {
  background: #dcf7ea;
  color: #087443;
}

.abc-matrix {
  display: grid;
  grid-template-columns: 58px repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 0;
}

.abc-matrix-body {
  margin: auto 0 18px;
}

.abc-matrix-items-button {
  background: #ffffff;
  border: 1px solid #dde3ee;
  border-radius: 6px;
  color: #475467;
  cursor: pointer;
  display: block;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  margin: 14px auto 0;
  padding: 8px 12px;
}

.abc-matrix-items-button:hover {
  border-color: var(--dashboard-primary);
  color: var(--dashboard-primary-text);
}

.abc-matrix-corner {
  color: #667085;
  font-size: 10px;
  line-height: 1.1;
  min-height: 38px;
  position: relative;
}

.abc-matrix-corner span {
  bottom: 0;
  left: 0;
  position: absolute;
}

.abc-matrix-corner strong {
  font-size: 10px;
  font-weight: 700;
  position: absolute;
  right: 0;
  top: 0;
}

.abc-matrix-corner::after {
  background: #d0d5dd;
  content: "";
  height: 1px;
  left: 7px;
  position: absolute;
  right: 7px;
  top: 19px;
  transform: rotate(-32deg);
}

.abc-matrix-header,
.abc-matrix-row-label {
  align-items: center;
  color: #475467;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  justify-content: center;
}

.abc-matrix-row-label {
  justify-content: flex-end;
  padding-right: 8px;
}

.abc-matrix-cell {
  border: 1px solid #e4e7ec;
  border-radius: 7px;
  cursor: default;
  min-height: 76px;
  padding: 10px;
  position: relative;
  text-align: left;
}

.abc-matrix-cell strong,
.abc-matrix-cell > span:not(.abc-matrix-category-tooltip),
.abc-matrix-cell small {
  display: block;
}

.abc-matrix-cell strong {
  color: #172033;
  font-size: 13px;
}

.abc-matrix-category {
  display: block !important;
}

.abc-matrix-category-tooltip {
  background: rgba(23, 32, 51, 0.95);
  border-radius: 5px;
  color: #ffffff !important;
  display: none !important;
  font-size: 11px;
  font-weight: 500;
  left: 10px;
  line-height: 1.35;
  max-width: 320px;
  min-width: 280px;
  padding: 8px 10px;
  position: absolute;
  top: 34px;
  white-space: normal;
  z-index: 45;
}

.abc-matrix-cell[data-revenue-class="B"] .abc-matrix-category-tooltip {
  left: 50%;
  transform: translateX(-50%);
}

.abc-matrix-cell[data-revenue-class="C"] .abc-matrix-category-tooltip {
  left: auto;
  right: 10px;
}

.abc-matrix-cell:hover .abc-matrix-category-tooltip {
  display: block !important;
}

.abc-matrix-cell > span:not(.abc-matrix-category-tooltip) {
  color: #172033;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  margin-top: 8px;
}

.abc-matrix-cell small {
  color: #667085;
  font-size: 11px;
  margin-top: 2px;
}

.abc-matrix-cell.critical {
  background: #dcf7ea;
  border-color: #a7e7c4;
}

.abc-matrix-cell.strategic {
  background: #fff4d6;
  border-color: #ffd982;
}

.abc-matrix-cell.middle {
  background: #f8fafc;
}

.abc-matrix-cell.low {
  background: #ffefea;
  border-color: #ffc7b8;
}

.abc-matrix-legend {
  border-top: 1px solid #edf1f7;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
  margin-top: auto;
  padding-top: 14px;
}

.abc-matrix-color-legend {
  align-items: center;
  display: flex;
  gap: 7px;
  min-width: 0;
}

.abc-color-dot {
  border-radius: 50%;
  flex: 0 0 9px;
  height: 9px;
  width: 9px;
}

.abc-color-dot.critical {
  background: #11a36a;
}

.abc-color-dot.strategic {
  background: #ffb020;
}

.abc-color-dot.middle {
  background: #d0d5dd;
}

.abc-color-dot.low {
  background: #f0512d;
}

.abc-matrix-color-legend strong {
  color: #172033;
  font-size: 12px;
  white-space: nowrap;
}

.abc-matrix-color-legend small {
  color: #667085;
  font-size: 11px;
  min-width: 0;
}

.abc-matrix-items-modal {
  box-sizing: border-box;
  max-width: 760px;
  overflow-x: hidden;
  width: min(760px, calc(100vw - 72px));
}

>>> .abc-matrix-items-modal-root.modal {
  z-index: 11000;
}

>>> .abc-matrix-items-modal-root .modal-background {
  z-index: 11000;
}

>>> .abc-matrix-items-modal-root .animation-content {
  max-width: calc(100vw - 40px);
  overflow-x: hidden;
  position: relative;
  z-index: 11001;
}

>>> .abc-matrix-items-modal-root .modal-card-body {
  overflow-x: hidden;
}

.abc-matrix-items-group {
  border-bottom: 1px solid #edf1f7;
  padding: 12px 0;
}

.abc-matrix-items-group:first-child {
  padding-top: 0;
}

.abc-matrix-items-group:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.abc-matrix-items-group-title {
  border: 1px solid #e4e7ec;
  border-radius: 7px;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 10px;
  margin-bottom: 8px;
  padding: 9px 10px;
}

.abc-matrix-items-group-title.critical {
  background: #dcf7ea;
  border-color: #a7e7c4;
}

.abc-matrix-items-group-title.strategic {
  background: #fff4d6;
  border-color: #ffd982;
}

.abc-matrix-items-group-title.middle {
  background: #f8fafc;
}

.abc-matrix-items-group-title.low {
  background: #ffefea;
  border-color: #ffc7b8;
}

.abc-matrix-items-group-title strong {
  color: #172033;
  font-size: 15px;
  line-height: 1.25;
}

.abc-matrix-items-group-copy {
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
}

.abc-matrix-items-group-title span,
.abc-matrix-items-group-title small,
.abc-matrix-items-empty,
.abc-matrix-items-row small {
  color: #667085;
  font-size: 11px;
  line-height: 1.3;
}

.abc-matrix-items-group-title span {
  min-width: 0;
  white-space: normal;
}

.abc-matrix-items-group-title small {
  flex: 0 0 auto;
  font-weight: 700;
}

.abc-matrix-items-list {
  display: grid;
  gap: 6px;
}

.abc-matrix-items-row {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-height: 20px;
}

.abc-matrix-items-row span {
  color: #172033;
  font-size: 12px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.abc-matrix-items-row small {
  flex: 0 0 auto;
}

.abc-matrix-items-footer {
  justify-content: flex-end;
}

.abc-matrix-empty {
  align-items: center;
  border: 1px dashed #d0d5dd;
  border-radius: 8px;
  color: #667085;
  display: flex;
  font-size: 13px;
  justify-content: center;
  line-height: 1.4;
  margin-top: 18px;
  min-height: 180px;
  padding: 18px;
  text-align: center;
}

.product-chart-title {
  align-items: flex-start;
  gap: 12px;
}

.product-chart-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.empty-list {
  color: #667085;
  padding: 28px 0;
  text-align: center;
}

.transaction-row,
.store-row {
  padding: 6px 0;
  border-bottom: 1px solid #edf1f7;
}

.transaction-row {
  display: block;
}

.transaction-date-divider {
  color: #667085;
  font-size: 10px;
  font-weight: 700;
  padding: 5px 0 2px;
  text-transform: uppercase;
}

.transaction-row strong,
.store-row strong {
  font-size: 13px;
}

.transaction-row > strong:first-child,
.store-info > span:last-child {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.transaction-value {
  color: #087443 !important;
  font-weight: 700;
}

.top-stores {
  position: relative;
}

.panel-title h2 {
  font-size: 14px;
  margin: 0;
}

.payment-panel-title {
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
}

.payment-total {
  margin-right: auto;
  white-space: nowrap;
}

.hourly-panel-title {
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
}

.hourly-panel-title h2 {
  margin-right: auto;
}

.hourly-comparison-delta {
  border-radius: 999px;
  font-size: 11px !important;
  font-weight: 700;
  line-height: 1;
  padding: 5px 7px;
  white-space: nowrap;
}

.hourly-comparison-delta.positive {
  background: #dcf7ea;
  color: #087443;
}

.hourly-comparison-delta.negative {
  background: #ffefea;
  color: #d24428;
}

.comparison-date-picker {
  width: 132px;
}

>>> .comparison-date-picker .input {
  height: 28px;
  border: 1px solid #dde3ee;
  border-radius: 6px;
  background: #ffffff;
  color: #172033;
  font-size: 12px;
  padding: 0 7px 0 30px;
}

>>> .comparison-date-picker .input:focus {
  border-color: var(--dashboard-primary);
  box-shadow: 0 0 0 2px var(--dashboard-primary-focus);
  outline: none;
}

>>> .comparison-date-picker .icon {
  height: 28px;
  width: 28px;
}

>>> .comparison-date-picker .dropdown-menu {
  z-index: 2000;
}

.payment-mode-toggle {
  display: inline-flex;
  border: 1px solid #dde3ee;
  border-radius: 6px;
  overflow: hidden;
  flex: 0 0 auto;
}

.payment-mode-toggle button {
  border: 0;
  border-left: 1px solid #dde3ee;
  background: #ffffff;
  color: #475467;
  cursor: pointer;
  font-size: 11px;
  line-height: 1;
  padding: 6px 7px;
}

.payment-mode-toggle button:first-child {
  border-left: 0;
}

.payment-mode-toggle button.active {
  background: var(--dashboard-primary);
  color: var(--dashboard-selected-text);
  font-weight: 700;
}

.panel-title.small {
  margin-top: 10px;
}

.ranking-panel-title {
  gap: 8px;
}

.ranking-mode-toggle {
  display: inline-flex;
  border: 1px solid #dde3ee;
  border-radius: 6px;
  overflow: hidden;
  flex: 0 0 auto;
}

.ranking-mode-toggle button {
  border: 0;
  border-left: 1px solid #dde3ee;
  background: #ffffff;
  color: #475467;
  cursor: pointer;
  font-size: 10px;
  line-height: 1;
  padding: 5px 6px;
}

.ranking-mode-toggle button:first-child {
  border-left: 0;
}

.ranking-mode-toggle button.active {
  background: var(--dashboard-primary);
  color: var(--dashboard-selected-text);
  font-weight: 700;
}

.store-row {
  gap: 10px;
  font-size: 11px;
  padding: 5px 0;
}

.store-row strong {
  font-size: 11px;
}

.store-info {
  gap: 6px;
  min-width: 0;
}

.store-position {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex: 0 0 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 10px;
}

.transaction-list-enter-active {
  transition: all 0.25s ease;
}

.transaction-list-enter {
  opacity: 0;
  transform: translateY(-8px);
}

.store-ranking-move {
  transition: transform 0.28s ease;
}

.store-ranking-forward-enter-active,
.store-ranking-forward-leave-active,
.store-ranking-back-enter-active,
.store-ranking-back-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.store-ranking-forward-enter,
.store-ranking-back-leave-to {
  opacity: 0;
  transform: translateX(14px);
}

.store-ranking-forward-leave-to,
.store-ranking-back-enter {
  opacity: 0;
  transform: translateX(-14px);
}

>>> .store-map-marker span {
  display: block;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--dashboard-primary);
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.28);
}

>>> .sale-map-pulse span {
  display: block;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--dashboard-primary-pulse);
  border: 2px solid var(--dashboard-primary);
  animation: sale-map-pulse 1.3s ease-out infinite;
}

@keyframes sale-map-pulse {
  0% {
    transform: scale(0.55);
    opacity: 1;
  }
  100% {
    transform: scale(1.9);
    opacity: 0;
  }
}

@media (max-width: 1100px) {
  .merchant-realtime-dashboard {
    display: block;
    height: auto;
    background: #f3f5f8;
    overflow: visible;
  }

  .dashboard-sidebar,
  .dashboard-sidebar.collapsed {
    width: 100%;
    min-height: auto;
    display: flex;
    overflow-x: auto;
  }

  .sidebar-header {
    display: none;
  }

  .dashboard-content {
    overflow: visible;
    border-top-left-radius: 0;
  }

  .dashboard-title-row {
    display: block;
  }

  .dashboard-scope-combobox {
    width: 100%;
    max-width: none;
    margin-top: 8px;
  }

  .dashboard-scope-dropdown {
    width: 100%;
    max-width: none;
  }

  .dashboard-grid,
  .charts-column,
  .sales-dashboard,
  .sales-charts-grid,
  .products-dashboard,
  .products-charts-grid {
    display: block;
    height: auto;
    overflow: visible;
  }

  .summary-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .map-panel,
  .chart-panel,
  .transactions-panel,
  .products-chart-panel {
    margin-bottom: 12px;
  }

  .chart-panel {
    height: 240px;
  }
}
</style>
